import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { App as AntApp, Input, Segmented, Button } from 'antd';
import SheetDate from '../components/SheetDate';
import AppBar from '../components/AppBar';
import { AsyncButton, Chip, RM, MoneyInput, groupMoney, parseMoney, markPicked } from '../components/kit';
import SheetSelect from '../components/SheetSelect';
import { AutosaveStatus, RestoredDraftBanner } from '../components/AutosaveStatus';
import usePagedOptions from '../hooks/usePagedOptions';
import useFormAutosave from '../hooks/useFormAutosave';
import { ListingLinkField } from '../components/ListingPickerSheet';
import PartyQuickCreate from '../components/PartyQuickCreate';
import { PartyChangeSheet } from '../components/PartyChangeReview';
import * as salesApi from '../api/sales';
import * as formsApi from '../api/bookingForms';
import * as contactsApi from '../api/contacts';
import * as listingsApi from '../api/listings';
import * as lettersApi from '../api/coAgencyLetters';
import * as projectsApi from '../api/projects';
import * as teamApi from '../api/team';
import { partyWords } from '../lib/partyWords';
import { SHARE_BASIS_OPTIONS, shareFromLetter, sharePayload, isShareComplete, letterShareSummary, dealSplitSummary, cidGciBase, REFERRAL_BASE_OPTIONS, referralBaseSummary } from '../lib/coAgencyShare';
import { partiesSnapshot, partiesDiff, partyChangeBody, partyChangeSavedText, PARTY_CHANGE_NOT_SAVED } from '../lib/partyChange';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';

// Same 10 property types the admin SaleFormPage uses (values === labels).
const PROPERTY_TYPE_OPTIONS = ['Terrace', 'Semi-D', 'Bungalow', 'Condo', 'Apartment', 'Townhouse', 'Commercial', 'Industrial', 'Land', 'Others']
  .map((v) => ({ value: v, label: v }));

// Dropdown label with creator attribution — the lookup returns the caller's own
// contact book, which includes parties HQ registered onto their deals, so non-own
// rows say whose they are.
// Downline-created registries show the creator in brackets, e.g. "Ali Lee (Agent Three)".
const contactLabel = (c) => (c.creator_name && !c.is_mine ? `${c.name} (${c.creator_name})` : c.name);
// Sheet rows carry a second line, so the identifier that actually disambiguates two
// similarly-named parties (IC / SSM / passport, phone) is visible before you pick.
// The lookup returns the registry's own column name (contact_no); older shapes and
// the contact detail call it `phone`.
const contactSub = (c) => [c.registration_no, c.contact_no || c.phone].filter(Boolean).join(' · ') || undefined;
// Rounding tolerance on the 100% split — the same 0.01 the admin portal and both
// servers use, so a split HQ accepts is never rejected here.
const SPLIT_TOLERANCE = 0.01;

/**
 * Money fields mirror the admin portal's InputNumber: an RM prefix and thousand
 * separators. A seven-figure price typed as an unbroken digit string is exactly where
 * a missing or extra zero hides, and this form's whole job is getting that number
 * right. `type="number"` cannot group digits, so this is a text input with a decimal
 * keypad that formats as you type and hands the caller back a clean numeric string.
 */
// groupMoney / parseMoney / MoneyInput now live in components/kit so the listing
// form enters a price exactly the way a sale does.
// Computed read-only amounts always show 2dp — a genuine RM 0.00 must read as zero,
// not as an empty field that looks like nothing was calculated.
const money2 = (n) => groupMoney(Number(n || 0).toFixed(2));

/**
 * Validation marks the FIELDS, not a toast.
 *
 * A toast names one problem, then disappears — and on this form the offending field
 * is usually on a step that isn't even on screen, so the agent is left hunting. So a
 * failed submit paints every bad field red at once, jumps to the step holding the
 * first one, and scrolls it into view. Errors clear themselves as the values become
 * valid, because they are recomputed on every render rather than frozen at submit.
 */
const FIELD_STEP = {
  sale_date: 1, property_address: 1, project: 1, unit: 1,
  gross: 3, comm_pct: 3, total_comm: 3, sst_rate: 3, tenancy_fee: 3,
  vendors: 2, purchasers: 2, ca: 2, split: 2,
};
// Co-agency rows are keyed per row (`ca:<id>`) so each bad row lights up on its own.
const fieldKind = (k) => (k.startsWith('ca:') ? 'ca' : k);
const stepOfField = (k) => FIELD_STEP[fieldKind(k)] || 1;
// Form order, so "the first error" means the topmost one on the page.
const FIELD_ORDER = ['sale_date', 'property_address', 'project', 'unit', 'vendors', 'purchasers', 'ca', 'split', 'gross', 'comm_pct', 'total_comm', 'sst_rate', 'tenancy_fee'];
const fieldRank = (k) => FIELD_ORDER.indexOf(fieldKind(k));
// Red border for the hand-rolled sheet triggers (SheetSelect / SheetDate), whose
// `style` prop lands last on the trigger.
const errorBorder = (on) => (on ? { borderColor: 'var(--bad)' } : undefined);

// Sub-headers inside the financial card, matching admin's BillingSectionHeader rows.
const subLabel = { fontSize: 11.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--muted)', marginTop: 2 };
// REFERRAL rows bill differently from co-agency rows: commission.service ignores
// `role` for them and always deducts the percentage off net GCI before agent splits.
// So a referral gets no lister/co-broke choice and no co-agency letter — only a rate.
const isReferral = (c) => c?.co_agency_type === 'REFERRAL';
const newCoAgencyRow = (id, name, type) => ({
  id,
  name,
  co_agency_type: type === 'REFERRAL' ? 'REFERRAL' : 'CO_AGENCY',
  // A share is agreed as a cut of the fee or as a flat sum. Percentage is the
  // default because it is what most deals are struck on — see lib/coAgencyShare.
  commission_basis: 'PERCENTAGE',
  commission_percentage: '',
  commission_amount: '',
  // Referrals have no lister/co-broke distinction — pinned to LISTER so the payload
  // shape stays identical downstream (exactly what the admin modal does).
  role: 'LISTER',
  // The other half of the agreement, live only when role is COBROKE: what CID's
  // portion of the deal is, when the other agency is the one collecting the fee.
  cid_share_basis: 'PERCENTAGE',
  cid_share_percentage: '',
  cid_share_amount: '',
  // Referrals only: which base the % is taken from — see lib/coAgencyShare.
  referral_base: 'CID_PORTION',
  co_agency_letter_id: null,
  co_agency_letter_ref: null,
});
// Project picker page size — the sheet scrolls the next page in.
const PROJECT_PAGE = 20;
// Party / agent / letter pickers page the same way.
const CONTACT_PAGE = 20;
const LETTER_PAGE = 20;
// What the pickers can actually see. Said out loud under the field, because "why
// isn't this vendor here?" has exactly one answer and it isn't obvious: the registry
// is the agent's own — what they registered plus the parties already on their deals,
// booking forms and letters. Anyone else's contact has to be created here.
const TEAM_SCOPE_HINT = 'Your contacts — ones you added, plus the parties on your own deals';
const CO_AGENCY_SCOPE_HINT = 'Co-agencies you added, plus the ones on your own deals and letters';
// Only AVAILABLE units can be transacted; the rest are listed but not selectable,
// exactly as the admin portal's unit dropdown disables them.
const UNIT_SELECTABLE = 'AVAILABLE';
// Sentinel option pinned at the top of every party picker → opens quick-create.
const CREATE_NEW = '__create_new__';
const createNewOption = (noun) => ({ value: CREATE_NEW, label: <span style={{ color: 'var(--primary)', fontWeight: 600 }}>＋ Create new {noun}…</span> });

// FULL-SCREEN · /sales/new — an agent drafts a sale, then saves it or submits to HQ.
// The creating agent is added by the server as the sole agent (split 100); multi-agent
// splits are an HQ/follow-up concern.
export default function SaleCreatePage() {
  const nav = useNavigate();
  const { id: editId } = useParams();
  const location = useLocation();
  const editing = !!editId;
  // Set when this screen was opened by "Convert to sale" on an issued OTP/OTR. It
  // prefills the form below and redirects the save through the convert endpoint.
  const fromBookingForm = editing ? null : (location.state?.fromBookingForm || null);
  // Set when opened by "Create sale from this co-share" on an accepted request. The
  // listing and both agents come from the request; `co_share_request_id` rides along
  // on the payload, and the server is what actually pins the requesting agent into
  // sale_agents and spends the request — this side only prefills.
  const fromCoShare = editing ? null : (location.state?.fromCoShare || null);
  // Set when opened by a PROJECT listing's "Create sale" (the effect below prefills).
  const fromProject = editing ? null : (location.state?.fromProject || null);
  const { message } = AntApp.useApp();
  const { agent: me } = useAuth();
  const [loaded, setLoaded] = useState(!editing); // edit mode waits for the raw sale to load
  // Whether the form holds everything it was opened with — the edit load, or a
  // prefill handed over from a booking form, co-share or project, including the
  // records those fetch afterwards. A device copy is restored only once it does,
  // so a late prefill can never land on top of the restored typing.
  const [settled, setSettled] = useState(() => !editing && !fromBookingForm && !fromCoShare?.listing_id && !fromProject?.project_id);
  // The loaded sale's updated_at, when the edit payload carries one — a device copy
  // older than it is stale.
  const [serverUpdatedAt, setServerUpdatedAt] = useState(null);
  // Mirrors admin: 'full' while Pending; 'invoiced' once Converted (info fields +
  // the figures; deal type and property link stay locked); 'limited' (info fields
  // only) for an ERP-imported ledger.
  const [editLevel, setEditLevel] = useState('full');
  const limited = editing && editLevel === 'limited';
  const invoiced = editing && editLevel === 'invoiced';
  // Either way the deal's structure is fixed: no deal type or listing / unit.
  const structureLocked = limited || invoiced;
  // An invoiced sale's co-agencies, referrals and agent splits can still change
  // (owner, 2026-10-09) — not through Save's PUT, which refuses them, but through
  // HQ's "what changes" check (lib/partyChange). The server says when.
  const [partiesAfterInvoice, setPartiesAfterInvoice] = useState(false);
  const partiesOpen = invoiced && partiesAfterInvoice;
  // The two lists as the edit load returned them, to tell a party change apart
  // from a figures-only save. And the info fields' payload as loaded, so a party
  // change knows whether anything else has to be saved first.
  const loadedParties = useRef(null);
  const loadedInfo = useRef(null);
  // The open "what changes" check: { preview, body, savedFirst } or null.
  const [partyReview, setPartyReview] = useState(null);
  // HQ finance typed the commission lines in by hand, so a figure change will not move them.
  const [commissionHandSet, setCommissionHandSet] = useState(false);
  // Create is a 3-step wizard (Property → Parties → Price); edit stays single-scroll.
  const wizard = !editing;
  const [step, setStep] = useState(1);

  const [dealType, setDealType] = useState('SUBSALES');
  const [saleDate, setSaleDate] = useState(dayjs());
  const [propertyAddress, setPropertyAddress] = useState('');

  // financial (two-way commission % ↔ total)
  const [gross, setGross] = useState('');
  const [commPct, setCommPct] = useState('');
  const [totalComm, setTotalComm] = useState('');
  const lastEdit = useRef('pct');
  const [vendorPayTax, setVendorPayTax] = useState(true);
  const [sstRate, setSstRate] = useState(8);
  // The five figures as GET /sales/:id/edit returned them. An invoiced save sends
  // the money only when the agent changed one of these: a remarks-only save must
  // not post the form's own SST / net GCI sums back, or HQ records a money change
  // (stored 719.99 vs the form's 720.00), recalculates commission, and the save
  // fails outright whenever HQ is unreachable.
  const loadedFigures = useRef(null);

  // parties
  const [vendors, setVendors] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  // [{ id, name, co_agency_type, commission_basis, commission_percentage, commission_amount, role, co_agency_letter_id, co_agency_letter_ref }]
  const [coAgencies, setCoAgencies] = useState([]);

  // sale agents + commission split — the caller starts as sole agent at 100%, and can add
  // co-agents to split (same as admin). Splits must total 100% before submit.
  const [saleAgents, setSaleAgents] = useState(() => (me?.id
    ? [{ agent_id: me.id, name: me.display_name || me.full_name || 'You', split_percentage: 100, self: true }] : []));
  useEffect(() => {
    if (me?.id) setSaleAgents((xs) => (xs.length ? xs : [{ agent_id: me.id, name: me.display_name || me.full_name || 'You', split_percentage: 100, self: true }]));
  }, [me]);

  // property — `listing` is the full picked row (address, price, photo) so the
  // linked-listing card can render without re-fetching; null while an edit-mode
  // load is still in flight.
  const [listingId, setListingId] = useState(null);
  const [listing, setListing] = useState(null);
  const [propertyType, setPropertyType] = useState(undefined);
  const [builtUpArea, setBuiltUpArea] = useState('');
  const [builtUpType, setBuiltUpType] = useState('');

  // project (PROJECT deal type) — pick a project + unit; price/commission auto-fill
  const [projectId, setProjectId] = useState(null);
  const [projectOpts, setProjectOpts] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectHasMore, setProjectHasMore] = useState(false);
  const [projectLoadingMore, setProjectLoadingMore] = useState(false);
  const [project, setProject] = useState(null);
  const [unitId, setUnitId] = useState(null);
  const [unitOpts, setUnitOpts] = useState([]);
  // Whether the picked project's unit list has come back — an empty list is only
  // "no units uploaded" once it has, not while it is still loading.
  const [unitsLoaded, setUnitsLoaded] = useState(false);

  // others
  const [tenancyPeriod, setTenancyPeriod] = useState('');
  const [tenancyFrom, setTenancyFrom] = useState(null);
  const [tenancyTo, setTenancyTo] = useState(null);
  // Tenancy To = From + Period − 1 day (a 2-year term starting 1 Jan 2026 ends
  // 31 Dec 2027). Driven off the two change handlers rather than an effect, so
  // loading a sale for editing never rewrites a stored end date — only actually
  // changing the period or start date does. Periods are held in months so
  // half-year terms (0.5 → 6 months) land exactly. Matches the admin portal.
  const deriveTenancyTo = (period, from) => {
    const months = Math.round((Number(period) || 0) * 12);
    if (!months || months < 0 || !from) return;
    setTenancyTo(from.add(months, 'month').subtract(1, 'day'));
  };
  const onTenancyPeriod = (v) => { setTenancyPeriod(v); deriveTenancyTo(v, tenancyFrom); autosave.noteChange(); };
  const onTenancyFrom = (d) => { setTenancyFrom(d); deriveTenancyTo(tenancyPeriod, d); autosave.noteChange(); };
  // Tenancy fee. The agent enters the three facts; the admin fee and what they
  // are owed are DERIVED — balance_of_tenancy_payable is a generated column
  // server-side, so it is never sent, only mirrored here for a live preview.
  const [tenancyFee, setTenancyFee] = useState('');
  const [tenancyDuty, setTenancyDuty] = useState('');
  const [tenancyPaidBy, setTenancyPaidBy] = useState(null);
  // Which agent fronted the LHDN duty. The reimbursement is repaid to them in
  // full rather than split, so on a multi-agent sale it has to be named.
  const [tenancyDutyAgent, setTenancyDutyAgent] = useState(null);
  const [tenancyLocked, setTenancyLocked] = useState(false);
  const [otherTerms, setOtherTerms] = useState('');
  const [loDate, setLoDate] = useState(null);
  const [loBank, setLoBank] = useState('');
  const [spaDate, setSpaDate] = useState(null);
  const [remarks, setRemarks] = useState('');
  // The number on the agent's OWN booking form — their pad, not the OTP/OTR this
  // app issues under Forms. Some agents check their cases in against that pad, so
  // it is the only thing the paper and this record have in common.
  const [bookingFormNo, setBookingFormNo] = useState('');

  // Quick-create dialog: { type: 'vendor'|'purchaser'|'coagency', sub } or null.
  const [quick, setQuick] = useState(null);

  // A setter as the screen's own controls call it: it also tells autosave the
  // agent changed something. Loads and prefills call the bare setters, which do
  // not — a freshly opened (or freshly converted) sale is not "unsaved".
  const byUser = (setter) => (v) => { setter(v); autosave.noteChange(); };

  // ── Edit mode: load the raw sale and prefill every field ─────────────────────
  useEffect(() => {
    if (!editing) return;
    salesApi.getSalesEditById(editId)
      .then((r) => {
        const d = r?.data ?? r;
        // `replace` so Back does not return to a screen that only bounces here again.
        if (!d.editable) { message.warning('This sale can no longer be edited.'); nav(`/sales/${editId}`, { replace: true }); return; }
        setEditLevel(d.edit_level || 'full');
        setPartiesAfterInvoice(!!d.parties_editable_after_invoice);
        setCommissionHandSet(!!d.commission_hand_set);
        setDealType(d.deal_type || 'SUBSALES');
        setSaleDate(d.sale_date ? dayjs(d.sale_date) : null);
        setPropertyAddress(d.property_address || '');
        setPropertyType(d.property_type || undefined);
        setBuiltUpArea(d.built_up_area || '');
        setBuiltUpType(d.built_up_type || '');
        setGross(d.gross_sales_amount != null ? String(d.gross_sales_amount) : '');
        setCommPct(d.commission_percentage != null ? String(d.commission_percentage) : '');
        setTotalComm(d.total_commission != null ? String(d.total_commission) : '');
        setVendorPayTax(!!d.vendor_pay_tax);
        setSstRate(d.sst_rate != null ? d.sst_rate : 8);
        loadedFigures.current = {
          gross: d.gross_sales_amount,
          commPct: d.commission_percentage,
          totalComm: d.total_commission,
          vendorPayTax: !!d.vendor_pay_tax,
          // The same default the field opens on, so an untouched blank rate is not a change.
          sstRate: d.sst_rate != null ? d.sst_rate : 8,
        };
        // Keep the identifiers the edit payload carries — a reopened draft's party
        // rows then read exactly like a freshly picked one, rather than a bare name.
        const party = (x) => ({ id: x.id, name: x.name, registration_no: x.registration_no || null, contact_no: x.contact_no || null });
        setVendors((d.vendors || []).map(party));
        setPurchasers((d.purchasers || []).map(party));
        const coRows = (d.co_agencies || []).map((c) => ({
          id: c.id,
          name: c.name,
          co_agency_type: c.co_agency_type === 'REFERRAL' ? 'REFERRAL' : 'CO_AGENCY',
          commission_basis: c.commission_basis || 'PERCENTAGE',
          commission_percentage: c.commission_percentage != null ? String(c.commission_percentage) : '',
          commission_amount: c.commission_amount != null ? String(c.commission_amount) : '',
          role: c.role || 'LISTER',
          cid_share_basis: c.cid_share_basis || 'PERCENTAGE',
          cid_share_percentage: c.cid_share_percentage != null ? String(c.cid_share_percentage) : '',
          cid_share_amount: c.cid_share_amount != null ? String(c.cid_share_amount) : '',
          referral_base: c.referral_base === 'AFTER_CO_AGENCY' ? 'AFTER_CO_AGENCY' : 'CID_PORTION',
          co_agency_letter_id: c.co_agency_letter_id || null,
          co_agency_letter_ref: c.co_agency_letter_ref || null,
        }));
        setCoAgencies(coRows);
        const agentRows = (d.agents || []).map((a) => ({ agent_id: a.agent_id, name: a.agent_id === me?.id ? (me.display_name || me.full_name || 'You') : a.name, split_percentage: a.split_percentage, self: a.agent_id === me?.id }));
        if (agentRows.length) setSaleAgents(agentRows);
        // No agents on the payload leaves the form's own default (the caller at 100%)
        // standing, so that is what "unchanged" means.
        loadedParties.current = partiesSnapshot(coRows, agentRows.length ? agentRows : (me?.id ? [{ agent_id: me.id, split_percentage: 100 }] : []));
        setListingId(d.listing_id || null);
        // The follow-up fetches below; the form counts as settled once they land.
        const waits = [];
        // Pull the linked listing so the card shows the real property rather than a
        // bare id; if it's since been withdrawn the card degrades to its label.
        if (d.listing_id) {
          waits.push(listingsApi.getListingsById(d.listing_id)
            .then((lr) => setListing(lr?.data ?? lr ?? null))
            .catch(() => {}));
        }
        setTenancyPeriod(d.tenancy_period || '');
        setTenancyFrom(d.tenancy_from ? dayjs(d.tenancy_from) : null);
        setTenancyTo(d.tenancy_to ? dayjs(d.tenancy_to) : null);
        setTenancyFee(d.tenancy_fee_collected || '');
        setTenancyDuty(d.tenancy_stamp_duty || '');
        setTenancyPaidBy(d.tenancy_duty_paid_by || null);
        setTenancyDutyAgent(d.tenancy_duty_paid_by_agent_id || null);
        setTenancyLocked(!!d.tenancy_locked);
        setOtherTerms(d.other_terms_conditions || '');
        setLoDate(d.lo_date ? dayjs(d.lo_date) : null);
        setLoBank(d.lo_bank || '');
        setSpaDate(d.spa_date ? dayjs(d.spa_date) : null);
        setRemarks(d.remarks || '');
        setBookingFormNo(d.agent_booking_form_no || '');
        if (d.deal_type === 'PROJECT' && d.project) {
          setProjectId(d.project.project_id);
          setProjectOpts([{ value: d.project.project_id, label: d.project.project_name || 'Project', data: null }]);
          // Pull the real project row: it names the field once a search has paged
          // the seeded option away, and carries the commission config a re-picked
          // unit prices itself from. Its SST config is deliberately NOT applied —
          // the sale's own stored values stand.
          waits.push(projectsApi.getProjectById(d.project.project_id)
            .then((pr) => setProject(pr?.data ?? pr ?? null))
            .catch(() => {}));
          // No unit when it was typed into the address (not in the uploaded list):
          // the sale still names its project, and that is what is restored.
          setUnitId(d.project_unit_id || null);
          if (d.project.unit_no) setUnitOpts([{ value: d.project_unit_id, label: d.project.unit_no, data: null }]);
          waits.push(fetchUnits(d.project.project_id));
        }
        setServerUpdatedAt(d.updated_at || null);
        setLoaded(true);
        Promise.allSettled(waits).then(() => setSettled(true));
      })
      .catch((e) => message.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, me]);

  const isRent = dealType === 'RENT';
  const isProject = dealType === 'PROJECT';
  // A project sale's commission and SST come from the project's config. Once
  // invoiced the unit can no longer be re-picked, so the figures open up for
  // correcting by hand instead.
  const fromUnit = isProject && !invoiced;
  // The price comes from a picked unit. A unit that is not in the uploaded list is
  // typed into the address instead, and then the agent types the price.
  const priceFromUnit = fromUnit && !!unitId;
  // The project whose commission rule the figures follow, once its row is here.
  const projectRule = fromUnit && project?.id && project.id === projectId ? project : null;
  // A rental's parties are a landlord and a tenant — the desktop build already names
  // them that way, and the picker headings here have to agree. See lib/partyWords.
  const words = partyWords(dealType);
  const totalN = parseFloat(totalComm) || 0;
  const sstRateN = parseFloat(sstRate) || 0;
  // SST / net GCI — mirror the admin formula EXACTLY (SaleFormPage): when the vendor pays,
  // SST is billed on top (net = full commission); when CID absorbs, SST is EXTRACTED from
  // the inclusive total so the net-GCI commission base matches admin.
  let sstAmount; let netGci;
  if (vendorPayTax) {
    sstAmount = Math.round(totalN * sstRateN) / 100;
    netGci = totalN;
  } else {
    sstAmount = Math.round((totalN - totalN / (1 + sstRateN / 100)) * 100) / 100;
    netGci = Math.round((totalN - sstAmount) * 100) / 100;
  }

  // commission_percentage is numeric(5,2) — deriving it to 4dp only showed the agent
  // a rate the row could never store. 2dp everywhere, exactly as admin's
  // precision={2} InputNumber does it.
  const pctOf = (total, g) => (g ? String(Number((total / g * 100).toFixed(2))) : '');
  const onPct = (v) => {
    lastEdit.current = 'pct';
    setCommPct(v);
    const g = parseFloat(gross) || 0;
    setTotalComm(g ? String(Number((g * (parseFloat(v) || 0) / 100).toFixed(2))) : '');
    autosave.noteChange();
  };
  const onTotal = (v) => {
    lastEdit.current = 'total';
    setTotalComm(v);
    setCommPct(pctOf(parseFloat(v) || 0, parseFloat(gross) || 0));
    autosave.noteChange();
  };
  const onGross = (v) => {
    setGross(v);
    // A project sale's commission follows the project's rule whatever the price: a
    // FIXED total stays put, a PERCENTAGE rate prices the new figure.
    if (projectRule) { applyProjectCommission(projectRule, v); autosave.noteChange(); return; }
    const g = parseFloat(v) || 0;
    if (lastEdit.current === 'pct') setTotalComm(g ? String(Number((g * (parseFloat(commPct) || 0) / 100).toFixed(2))) : '');
    else setCommPct(pctOf(parseFloat(totalComm) || 0, g));
    autosave.noteChange();
  };
  // A typed rate settles to 2dp on blur (and the total re-derives from the settled
  // figure), rather than being rewritten mid-keystroke. Only when that changes
  // something: tabbing through a field is not an edit.
  const normalizePct = () => {
    if (commPct === '') return;
    const n = parseFloat(commPct);
    if (Number.isNaN(n)) return;
    const settledPct = String(Number(n.toFixed(2)));
    if (settledPct !== String(commPct)) onPct(settledPct);
  };
  // Admin's SST rate is min 0 / max 100 / 2dp and cannot be left blank.
  const normalizeSstRate = () => {
    if (sstRate === '') return;
    const n = parseFloat(sstRate);
    if (Number.isNaN(n)) return;
    const settledRate = String(Number(Math.min(100, Math.max(0, n)).toFixed(2)));
    if (settledRate !== String(sstRate)) byUser(setSstRate)(settledRate);
  };

  const caIds = coAgencies.map((c) => c.id);
  const caLookup = usePagedOptions(async (q, page) => {
    const r = await contactsApi.listContactsLookup({
      type: 'CO_AGENCY', q, page, limit: CONTACT_PAGE,
      include_ids: caIds.join(',') || undefined,
    });
    // `tour` rides along so Learn Mode can spotlight a row INSIDE the picker
    // sheet — the sheet renders below the tour's blockers, so a step that opens
    // one is only finishable if the next step can anchor an option in it.
    return (r?.data ?? r ?? []).map((c) => ({ value: c.id, label: contactLabel(c), sub: contactSub(c), data: c, tour: c.tour }));
  });
  const addCoAgency = (id) => {
    if (id === CREATE_NEW) { setQuick({ type: 'coagency' }); return; }
    const d = caLookup.options.find((o) => o.value === id)?.data;
    if (d && !coAgencies.some((x) => x.id === id)) byUser(setCoAgencies)((xs) => [...xs, newCoAgencyRow(id, d.name, d.co_agency_type)]);
  };

  // Quick-create result → drop the new contact straight into the right party list.
  // A co-agency comes back with its type on `sub_type` (the registry's own column
  // name), which decides whether the new row bills as a referral.
  const onQuickCreated = (c) => {
    if (!c?.id) { setQuick(null); return; }
    autosave.noteChange();
    // The detail shape names the number `phone`; the party row shows contact_no.
    const fresh = { id: c.id, name: c.name, registration_no: c.registration_no || null, contact_no: c.contact_no || c.phone || null };
    if (quick?.type === 'vendor') setVendors((xs) => (xs.some((v) => v.id === c.id) ? xs : [...xs, fresh]));
    if (quick?.type === 'purchaser') setPurchasers((xs) => (xs.length >= 4 || xs.some((p) => p.id === c.id) ? xs : [...xs, fresh]));
    if (quick?.type === 'coagency') {
      setCoAgencies((xs) => (xs.some((x) => x.id === c.id) ? xs : [...xs, newCoAgencyRow(c.id, c.name, c.co_agency_type || c.sub_type)]));
    }
    setQuick(null);
  };
  const patchCoAgency = (id, patch) => byUser(setCoAgencies)((xs) => xs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const removeCoAgency = (id) => byUser(setCoAgencies)((xs) => xs.filter((c) => c.id !== id));

  // Sale-agent split directory search + row management.
  const agentIds = saleAgents.map((a) => a.agent_id);
  const agentLookup = usePagedOptions(async (q, page) => {
    const r = await teamApi.listAgentsLookup({ q, page, limit: CONTACT_PAGE });
    return (r?.data ?? r ?? []).map((a) => ({
      value: a.id,
      label: a.name,
      sub: [a.management_rank, a.ren_no ? `REN ${a.ren_no}` : null].filter(Boolean).join(' · ') || undefined,
      data: a,
    }));
  });
  const addAgent = (id) => {
    const d = agentLookup.options.find((o) => o.value === id)?.data;
    if (d && !saleAgents.some((a) => a.agent_id === id)) byUser(setSaleAgents)((xs) => [...xs, { agent_id: id, name: d.name, split_percentage: 0, self: false }]);
  };
  const patchAgentSplit = (id, val) => byUser(setSaleAgents)((xs) => xs.map((a) => (a.agent_id === id ? { ...a, split_percentage: val } : a)));
  const removeAgent = (id) => byUser(setSaleAgents)((xs) => {
    const next = xs.filter((a) => a.agent_id !== id);
    return next.length === 1 ? next.map((a) => ({ ...a, split_percentage: 100 })) : next;
  });
  const totalSplit = saleAgents.reduce((s, a) => s + (Number(a.split_percentage) || 0), 0);
  // Admin and both servers accept a hair of rounding drift on a 3-way split; matching
  // the tolerance here stops the PWA rejecting a 33.33×3 = 99.99 split that HQ takes.
  const splitOk = Math.abs(totalSplit - 100) <= SPLIT_TOLERANCE;
  // On an invoiced sale: which of the two lists the agent changed since the load.
  // Either one routes Save through HQ's "what changes" check instead of the PUT.
  const partyDiff = partiesOpen ? partiesDiff(loadedParties.current, coAgencies, saleAgents) : { coas: false, agents: false, any: false };
  // Flipping the role swaps which half of the row is live, so the half that no
  // longer applies is cleared — a leftover figure on the dormant half would state
  // terms the deal does not have. The linked letter goes with a co-broke too: a
  // letter setting the co-agency's sharing ratio contradicts a row where they are
  // the ones collecting.
  const setCoRole = (coId, role) => patchCoAgency(coId, role === 'COBROKE'
    ? { role, commission_basis: 'PERCENTAGE', commission_percentage: '0', commission_amount: '', cid_share_basis: 'PERCENTAGE', cid_share_percentage: '', cid_share_amount: '', co_agency_letter_id: null, co_agency_letter_ref: null }
    : { role, cid_share_basis: 'PERCENTAGE', cid_share_percentage: '', cid_share_amount: '' });
  // Link a verified co-agency letter to a row and adopt the share it states —
  // including WHICH of the two shapes that share is in, so a flat-sum letter fills
  // in a flat-sum row. Unlinking clears the figure with it (as admin does): the
  // number came from the letter, so leaving it behind states a share no document
  // backs.
  const linkLetter = (coId, letterId, lt) => patchCoAgency(coId, letterId
    ? {
      co_agency_letter_id: letterId,
      co_agency_letter_ref: lt?.letter_ref || null,
      ...(lt ? shareFromLetter(lt) : {}),
    }
    : { co_agency_letter_id: null, co_agency_letter_ref: null, ...shareFromLetter(null) });

  // Link-to-listing → auto-fills the property block on pick. The picker browses
  // ACTIVE inventory only (never drafts, and never a property already UNDER_OFFER
  // to someone else's deal), own + co-broke, filtered to the listing type matching
  // the deal type. AUCTION deals transact sub-sale stock, so they browse SUBSALE —
  // the same mapping the admin portal's ListingSelect uses.
  const LISTING_TYPE_BY_DEAL = { SUBSALES: 'SUBSALE', RENT: 'RENT', AUCTION: 'SUBSALE', PROJECT: 'PROJECT' };
  // A linked listing OWNS the property facts: address, type, built-up and price are
  // copied from it and then locked, so a sale can never describe the property
  // differently from the listing it was transacted off (admin does the same).
  // Unlink to edit them by hand — which is why the lock only applies while the link
  // field is on screen: on a Converted sale the link is hidden, and locking there
  // would strand the info fields HQ deliberately leaves the agent to correct.
  const linkedToListing = !!listingId && !structureLocked;
  // A listing linked by a booking form or a co-share request arrives after the
  // first render. Its property type and built-up area are locked "From listing",
  // so they are filled from it here — never over what is already there (a draft,
  // a sale being edited).
  const fillFromLinkedListing = (l) => {
    if (!l) return;
    setPropertyType((t) => t || l.property_type || undefined);
    setBuiltUpArea((a) => a || (l.built_up_sqft != null ? String(l.built_up_sqft) : ''));
  };
  const onPickListing = (l) => {
    autosave.noteChange();
    setListingId(l?.id || null);
    setListing(l || null);
    if (!l) {
      // Unlink clears what the listing filled in, rather than stranding another
      // property's address in a now-unlinked sale.
      setPropertyAddress('');
      setPropertyType(undefined);
      setBuiltUpArea('');
      onGross('');
      return;
    }
    setPropertyAddress(l.property_address || '');
    setPropertyType(l.property_type || undefined);
    setBuiltUpArea(l.built_up_sqft != null ? String(l.built_up_sqft) : '');
    onGross(l.asking_price_rm != null ? String(l.asking_price_rm) : '');
  };

  // Changing the deal type invalidates everything downstream of it — a linked
  // SUBSALE listing, a project unit, the price it filled in, the tenancy dates that
  // only exist for RENT. Admin wipes all of it on the same event; doing it in the
  // handler (not an effect) means an edit-mode load or a from-project prefill, both
  // of which set the deal type programmatically, never trip the reset.
  const onDealType = (v) => {
    if (v === dealType) return;
    // During a conversion the booking form owns the deal type. The control is already
    // disabled; this stops any other route from firing the wipe below and clearing a
    // prefill the agent did not ask to lose.
    if (fromBookingForm) return;
    autosave.noteChange();
    setDealType(v);
    setListingId(null); setListing(null);
    setProjectId(null); setProject(null); setProjectOpts([]); setUnitId(null); setUnitOpts([]);
    setPropertyAddress(''); setPropertyType(undefined); setBuiltUpArea(''); setBuiltUpType('');
    lastEdit.current = 'pct';
    setGross(''); setCommPct(''); setTotalComm('');
    // Back to a fresh form's defaults rather than blank — a project pick overrides
    // both, and every other deal type bills SST on top at 8%.
    setVendorPayTax(true); setSstRate(8);
    setTenancyPeriod(''); setTenancyFrom(null); setTenancyTo(null); setOtherTerms('');
    setLoDate(null); setLoBank(''); setSpaDate(null);
    setRemarks('');
  };

  // PROJECT deal type — mirrors the admin SaleFormPage: pick a project → load its units →
  // pick a unit → auto-fill price/built-up and commission from the project's config.
  // The project list pages: search narrows it server-side, and scrolling the sheet
  // pulls the next page, so a long developer book is fully reachable.
  const projectQ = useRef('');
  const projectBusy = useRef(false);
  const fetchProjects = async (p, q, replace) => {
    // A new search always wins; only a load-more waits its turn.
    if (projectBusy.current && !replace) return;
    projectBusy.current = true;
    if (!replace) setProjectLoadingMore(true);
    try {
      const r = await projectsApi.listProjects({ search: q || undefined, page: p, limit: PROJECT_PAGE });
      const rows = (r?.data ?? r) || [];
      const opts = rows.map((x) => ({ value: x.id, label: x.name, data: x }));
      setProjectOpts((prev) => (replace
        ? opts
        : [...prev, ...opts.filter((o) => !prev.some((x) => x.value === o.value))]));
      setProjectPage(p);
      const total = r?.pagination?.totalPages;
      setProjectHasMore(total != null ? p < total : rows.length >= PROJECT_PAGE);
    } catch { /* leave the list as it stands */ }
    finally { projectBusy.current = false; setProjectLoadingMore(false); }
  };
  const searchProjects = (q) => { projectQ.current = q; fetchProjects(1, q, true); };
  const loadMoreProjects = () => { if (!projectBusy.current) fetchProjects(projectPage + 1, projectQ.current, false); };

  // The picked project's units. Resolves to the option rows (empty on a failed
  // fetch) so a prefill can pick its unit out of them.
  const fetchUnits = (pid) => {
    setUnitsLoaded(false);
    return projectsApi.listProjectUnits(pid, { limit: 300 })
      .then((ur) => {
        const opts = ((ur?.data ?? ur) || []).map(unitOptionRow);
        setUnitOpts(opts);
        return opts;
      })
      .catch(() => [])
      .finally(() => setUnitsLoaded(true));
  };
  const onPickProject = async (id) => {
    autosave.noteChange();
    setProjectId(id || null); setUnitId(null); setUnitOpts([]); setUnitsLoaded(false);
    const d = projectOpts.find((o) => o.value === id)?.data || null;
    setProject(d);
    if (!id) return;
    // A PROJECT sale takes its SST treatment from the project, not the form's
    // defaults — same as admin, which pulls both the moment a project is picked.
    // The commission too: with no unit picked it is priced on the typed price.
    if (d) { setVendorPayTax(!!d.vendor_pay_tax); setSstRate(d.sst_rate ?? 8); applyProjectCommission(d, gross); }
    await fetchUnits(id);
  };
  // The commission from the project's config, priced on `price`: a PERCENTAGE
  // project fixes the rate (the total follows the price), a FIXED one the total.
  // Used for a picked unit's net price and, when the unit is typed into the
  // address instead, for the price the agent types.
  const applyProjectCommission = (proj, price) => {
    const p = parseFloat(price) || 0;
    if (proj?.commission_type === 'PERCENTAGE') {
      const rate = Number(proj.commission_rate) || 0;
      lastEdit.current = 'pct';
      setCommPct(String(rate));
      setTotalComm(p ? String(Number((p * rate / 100).toFixed(2))) : '');
    } else if (proj?.commission_type === 'FIXED') {
      const t = Number(proj.commission_fixed_amount) || 0;
      lastEdit.current = 'total';
      setTotalComm(String(t));
      setCommPct(pctOf(t, p));
    }
  };
  // Re-apply the rule whenever the project's row lands: on a pick, and also when a
  // draft or sale is reopened (edit load, restored device copy), where nothing else
  // would — a FIXED project's total then drifted with the price as a derived rate.
  useEffect(() => {
    if (projectRule) applyProjectCommission(projectRule, gross);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectRule?.id, projectRule?.commission_type, projectRule?.commission_rate, projectRule?.commission_fixed_amount]);
  // Fill price / built-up / commission from a unit + its project's commission
  // config. Shared by the manual unit picker and the from-listing prefill (where
  // the `project` state hasn't committed yet, so the project is passed in).
  const applyUnit = (u, proj) => {
    const netPrice = u.net_price_rm || u.spa_price_rm || 0;
    if (u.built_up_sqft != null) setBuiltUpArea(String(u.built_up_sqft));
    setGross(String(netPrice));
    applyProjectCommission(proj, netPrice);
  };
  const onPickUnit = (id) => {
    autosave.noteChange();
    setUnitId(id || null);
    const u = unitOpts.find((o) => o.value === id)?.data;
    if (u) applyUnit(u, project);
    // Cleared: the price stays as it was, now open to typing, and the commission
    // keeps following the project's config on it.
    else if (!id) applyProjectCommission(project, gross);
  };

  // Sheet rows have room for what a dropdown line never did — type, size, price and
  // status all sit under the unit number instead of being crammed into one
  // truncated label. Reserved / sold / on-hold units are shown but greyed out and
  // unpickable, so an agent can see the unit exists and why it's unavailable
  // instead of hunting for a unit number that silently isn't in the list.
  const unitOptionRow = (u) => {
    const price = u.net_price_rm || u.spa_price_rm;
    return {
      value: u.id,
      label: u.unit_no,
      disabled: u.status !== UNIT_SELECTABLE,
      sub: [
        u.unit_type,
        u.built_up_sqft != null ? `${Number(u.built_up_sqft).toLocaleString('en-US')} sqft` : null,
        price ? RM(price) : null,
        u.status || null,
      ].filter(Boolean).join(' · ') || undefined,
      search: `${u.unit_no || ''} ${u.unit_type || ''} ${u.block || ''}`,
      data: u,
    };
  };

  /**
   * Arrived from an accepted co-share: the property is the lister's own listing and
   * the split was already agreed between the two agents, so both are filled in.
   *
   * The split is seeded from `agreed_split_percentage` (the requester's share) with
   * the remainder to the caller, which is exactly what the server would fall back to
   * — so what the agent sees on screen is what saving would produce either way. It
   * stays editable: the agreement is a starting point, not a lock, and the server
   * only insists that the requesting agent is still IN the split.
   */
  useEffect(() => {
    const cs = fromCoShare;
    if (!cs?.id || editing) return;
    setDealType(cs.listing_type === 'RENT' ? 'RENT' : 'SUBSALES');
    if (cs.property_address) setPropertyAddress((s) => s || cs.property_address);
    if (cs.asking_price_rm != null) setGross((g) => g || String(cs.asking_price_rm));
    if (cs.listing_id) {
      setListingId(cs.listing_id);
      listingsApi.getListingsById(cs.listing_id)
        .then((r) => { const l = r?.data ?? r; setListing(l); fillFromLinkedListing(l); })
        .catch(() => { /* the card degrades to its label */ })
        .finally(() => setSettled(true));
    }
    const share = Number(cs.agreed_split_percentage);
    const theirs = Number.isFinite(share) ? share : 50;
    const meId = cs.lister?.id || me?.id;
    if (meId && cs.requester?.id) {
      setSaleAgents([
        { agent_id: meId, name: me?.display_name || me?.full_name || 'You', split_percentage: Number((100 - theirs).toFixed(2)), self: true },
        { agent_id: cs.requester.id, name: cs.requester.name, split_percentage: theirs, self: false, coShare: true },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  // Arrived from a PROJECT listing's "Create sale" (whole project or one unit):
  // switch to a PROJECT deal with the project — and unit, when given — already
  // selected and price/commission auto-filled, exactly as if picked by hand.
  useEffect(() => {
    const fp = location.state?.fromProject;
    if (editing || !fp?.project_id) return;
    (async () => {
      try {
        const pr = await projectsApi.getProjectById(fp.project_id);
        const proj = pr?.data ?? pr;
        if (!proj?.id) return;
        setDealType('PROJECT');
        setProject(proj);
        setProjectId(proj.id);
        setProjectOpts([{ value: proj.id, label: proj.name, data: proj }]);
        setVendorPayTax(!!proj.vendor_pay_tax);
        setSstRate(proj.sst_rate ?? 8);
        if (fp.property_address) setPropertyAddress((s) => s || fp.property_address);
        const opts = await fetchUnits(proj.id);
        const u = fp.unit_id ? opts.find((o) => o.value === fp.unit_id)?.data : null;
        if (u) { setUnitId(fp.unit_id); applyUnit(u, proj); }
        // The whole project, unit to be typed into the address: its commission
        // config still applies, priced once the agent types the price.
        else applyProjectCommission(proj, '');
      } catch { /* fall back to manual pick */ }
      finally { setSettled(true); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Arrived from an issued OTP/OTR's "Convert to sale": the same create screen, with
  // everything the booking form already established filled in — deal type, property,
  // price, the locked listing, and the parties.
  //
  // The parties are the point. They were chosen by hand when the form was written, and
  // re-picking the same purchaser and vendor to turn that form into a sale was pure
  // re-entry. RENT forms name tenants and landlords; SALES forms name purchasers and
  // owners — both map onto the sale's purchasers/vendors. Ids are resolved to full
  // contacts so the party rows render names, not blanks.
  //
  // A contact that has since been deleted is skipped rather than failing the prefill:
  // an agent who has to re-pick one party is better off than one facing a blank screen.
  useEffect(() => {
    const bf = fromBookingForm;
    if (!bf?.id) return;
    const fd = bf.form_data || {};
    const rent = bf.sale_type === 'RENT';

    setDealType(rent ? 'RENT' : 'SUBSALES');
    if (fd.property_address) setPropertyAddress((s) => s || fd.property_address);
    const price = rent ? fd.rental_rm : fd.selling_price_rm;
    if (price != null && price !== '') setGross((g) => g || String(price));
    if (fd.other_terms) setOtherTerms((s) => s || fd.other_terms);

    // An OTR already fixes the term and its dates — the same three fields the sale
    // asks for, in the same units (period in years). Set directly rather than through
    // the onTenancy* handlers, which would re-derive "to" from "from" and overwrite an
    // end date the parties actually agreed to.
    if (rent) {
      if (fd.tenancy_period != null && fd.tenancy_period !== '') setTenancyPeriod((s) => s || String(fd.tenancy_period));
      if (fd.tenancy_from && dayjs(fd.tenancy_from).isValid()) setTenancyFrom((d) => d || dayjs(fd.tenancy_from));
      if (fd.tenancy_to && dayjs(fd.tenancy_to).isValid()) setTenancyTo((d) => d || dayjs(fd.tenancy_to));
    }

    const waits = [];
    if (bf.listing_id) {
      setListingId(bf.listing_id);
      waits.push(listingsApi.getListingsById(bf.listing_id)
        .then((r) => { const l = r?.data ?? r; setListing(l); fillFromLinkedListing(l); })
        .catch(() => { /* the card just stays collapsed */ }));
    }

    const purchaserIds = (rent ? [fd.tenant_1_id, fd.tenant_2_id] : [fd.purchaser_1_id, fd.purchaser_2_id]).filter(Boolean);
    const vendorIds = (rent ? [fd.landlord_1_id, fd.landlord_2_id] : [fd.owner_1_id, fd.owner_2_id]).filter(Boolean);
    const one = (fetcher) => (cid) => fetcher(cid).then((r) => r?.data ?? r).catch(() => null);
    const party = (x) => ({ id: x.id, name: x.name, registration_no: x.registration_no || null, contact_no: x.contact_no || null });

    waits.push(Promise.all([
      Promise.all(purchaserIds.map(one(contactsApi.getPurchasersById))),
      Promise.all(vendorIds.map(one(contactsApi.getVendorsById))),
    ]).then(([ps, vs]) => {
      const cleanP = ps.filter((x) => x?.id).map(party);
      const cleanV = vs.filter((x) => x?.id).map(party);
      if (cleanP.length) setPurchasers((xs) => (xs.length ? xs : cleanP));
      if (cleanV.length) setVendors((xs) => (xs.length ? xs : cleanV));
    }));
    Promise.allSettled(waits).then(() => setSettled(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Converted sales: only the HQ-unlocked info fields go over the wire.
  const buildLimitedPayload = () => ({
    sale_date: saleDate ? saleDate.format('YYYY-MM-DD') : null,
    property_address: propertyAddress.trim() || null,
    property_type: propertyType || null,
    built_up_area: builtUpArea.trim() || null,
    built_up_type: builtUpType.trim() || null,
    ...(isRent
      ? {
        tenancy_period: tenancyPeriod ? Number(tenancyPeriod) : null,
        tenancy_from: tenancyFrom ? tenancyFrom.format('YYYY-MM-DD') : null,
        tenancy_to: tenancyTo ? tenancyTo.format('YYYY-MM-DD') : null,
        // Only the three inputs go over the wire — balance_of_tenancy_payable is
        // generated in Postgres and rejects writes. Once HQ has verified the
        // breakdown the server refuses these anyway, so they are dropped here to
        // keep a locked sale's other edits from being bounced with them.
        ...(tenancyLocked ? {} : {
          tenancy_fee_collected: tenancyFee === '' ? null : Number(tenancyFee),
          tenancy_stamp_duty: tenancyDuty === '' ? null : Number(tenancyDuty),
          tenancy_duty_paid_by: tenancyPaidBy || null,
          tenancy_duty_paid_by_agent_id: tenancyPaidBy === 'AGENT' ? (tenancyDutyAgent || null) : null,
        }),
        other_terms_conditions: otherTerms.trim() || null,
      }
      : { lo_date: loDate ? loDate.format('YYYY-MM-DD') : null, lo_bank: loBank.trim() || null, spa_date: spaDate ? spaDate.format('YYYY-MM-DD') : null }),
    remarks: remarks.trim() || null,
    // null, never '' — an empty string would store "no number" as a second value
    // in the column, and antd drops an `undefined` from the JSON altogether.
    agent_booking_form_no: bookingFormNo.trim() || null,
  });

  // Did the agent change a figure since the form loaded? Money compares to the cent,
  // the SST bearer exactly. SST amount and net GCI are not compared: the form
  // re-derives them, and that re-derivation on open is not an edit.
  const cents = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? Math.round(n * 100) : null;
  };
  const figuresChanged = () => {
    const b = loadedFigures.current;
    if (!b) return false;
    return cents(gross) !== cents(b.gross)
      || cents(commPct) !== cents(b.commPct)
      || cents(totalComm) !== cents(b.totalComm)
      || !!vendorPayTax !== b.vendorPayTax
      || cents(sstRate) !== cents(b.sstRate);
  };
  // The info fields' payload as the edit load filled them in. Taken once the loaded
  // values have rendered (every setter in the load lands in one batch), and before a
  // device copy can be restored over them.
  useEffect(() => {
    if (!editing || !loaded || loadedInfo.current != null) return;
    loadedInfo.current = JSON.stringify(buildLimitedPayload());
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const infoChanged = () => loadedInfo.current == null || JSON.stringify(buildLimitedPayload()) !== loadedInfo.current;
  // Invoiced sales: the info fields, plus the figures when — and only when — one of
  // them changed; then all seven go together so SST and net GCI stay consistent with
  // the rest. Never deal_type, listing / unit, agents or co-agencies — the server
  // refuses those once the invoice is out (a party change has its own check below).
  const buildInvoicedPayload = () => (figuresChanged() ? {
    ...buildLimitedPayload(),
    gross_sales_amount: parseFloat(gross) || 0,
    commission_percentage: parseFloat(commPct) || 0,
    total_commission: totalN,
    vendor_pay_tax: !!vendorPayTax,
    sst_rate: sstRateN,
    sst_amount: sstAmount,
    net_gci: netGci,
  } : buildLimitedPayload());

  const buildPayload = () => ({
    deal_type: dealType,
    sale_date: saleDate ? saleDate.format('YYYY-MM-DD') : null,
    // Raising the sale from an accepted co-share. The server re-checks that it is
    // accepted and the caller's to convert, forces the listing to the one on the
    // request, insists the requesting agent is in the split, and marks the request
    // spent — none of which depends on this field being honest, only on it being sent.
    ...(fromCoShare?.id ? { co_share_request_id: fromCoShare.id } : {}),
    listing_id: isProject ? null : (listingId || null),
    project_unit_id: isProject ? (unitId || null) : null,
    // Always the project for a PROJECT sale: with no unit picked (typed into the
    // address) it is the only link the sale has to its project.
    project_id: isProject ? (projectId || null) : null,
    property_address: propertyAddress.trim() || null,
    property_type: propertyType || null,
    built_up_area: builtUpArea.trim() || null,
    built_up_type: builtUpType.trim() || null,
    gross_sales_amount: parseFloat(gross) || 0,
    commission_percentage: parseFloat(commPct) || 0,
    total_commission: totalN,
    vendor_pay_tax: !!vendorPayTax,
    sst_rate: sstRateN,
    sst_amount: sstAmount,
    net_gci: netGci,
    agents: saleAgents.map((a) => ({ agent_id: a.agent_id, split_percentage: Number(a.split_percentage) || 0 })),
    vendors: vendors.map((v) => ({ vendor_id: v.id })),
    purchasers: purchasers.map((p) => ({ purchaser_id: p.id })),
    co_agencies: coAgencies.map((c) => ({
      co_agency_id: c.id,
      role: c.role || 'LISTER',
      co_agency_letter_id: c.co_agency_letter_id || null,
      ...sharePayload(c),
    })),
    ...(isRent
      ? {
        tenancy_period: tenancyPeriod ? Number(tenancyPeriod) : null,
        tenancy_from: tenancyFrom ? tenancyFrom.format('YYYY-MM-DD') : null,
        tenancy_to: tenancyTo ? tenancyTo.format('YYYY-MM-DD') : null,
        // Only the three inputs go over the wire — balance_of_tenancy_payable is
        // generated in Postgres and rejects writes. Once HQ has verified the
        // breakdown the server refuses these anyway, so they are dropped here to
        // keep a locked sale's other edits from being bounced with them.
        ...(tenancyLocked ? {} : {
          tenancy_fee_collected: tenancyFee === '' ? null : Number(tenancyFee),
          tenancy_stamp_duty: tenancyDuty === '' ? null : Number(tenancyDuty),
          tenancy_duty_paid_by: tenancyPaidBy || null,
          tenancy_duty_paid_by_agent_id: tenancyPaidBy === 'AGENT' ? (tenancyDutyAgent || null) : null,
        }),
        other_terms_conditions: otherTerms.trim() || null,
      }
      : { lo_date: loDate ? loDate.format('YYYY-MM-DD') : null, lo_bank: loBank.trim() || null, spa_date: spaDate ? spaDate.format('YYYY-MM-DD') : null }),
    remarks: remarks.trim() || null,
    // null, never '' — an empty string would store "no number" as a second value
    // in the column, and antd drops an `undefined` from the JSON altogether.
    agent_booking_form_no: bookingFormNo.trim() || null,
  });

  // Mirrors the admin portal SaleFormPage handleSave() checks exactly — an
  // agent-created sale must clear the same bar an admin-created one does. Recomputed
  // every render, so a field stops being red the moment it becomes valid.
  const collectErrors = () => {
    const e = {};
    if (!saleDate) e.sale_date = 'Sale date is required';
    if (!propertyAddress.trim()) e.property_address = 'Property address is required';
    // Checked before the `limited` bail-out: on a Converted sale the tenancy fee
    // is the one financial thing still open, and it is precisely when it gets
    // filled in. A duty above the fee would make the admin fee negative — the DB
    // rejects it outright, so catch it here rather than on a 500.
    if (isRent && !tenancyLocked && tenancyFee !== '' && Number(tenancyDuty || 0) > Number(tenancyFee)) {
      e.tenancy_duty = 'Stamp duty cannot exceed the tenancy fee collected';
    }
    if (limited) return e; // other financials/parties are locked — nothing more to validate
    // The unit is optional: one not in the uploaded list is typed into the address.
    if (isProject && !projectId && !invoiced) e.project = 'Project is required';
    if (!(parseFloat(gross) > 0)) e.gross = isRent ? 'Rental amount is required' : 'Gross sales amount is required';
    if (!fromUnit && !(parseFloat(commPct) >= 0)) e.comm_pct = 'Commission rate is required';
    if (!(parseFloat(totalComm) >= 0)) e.total_comm = 'Total commission is required';
    // Admin makes the SST rate a required 0–100 field. Left blank it silently became
    // 0, which zeroes the SST and quietly changes the net GCI.
    if (sstRate === '' || Number.isNaN(parseFloat(sstRate))) e.sst_rate = 'SST rate is required';
    else if (sstRateN < 0 || sstRateN > 100) e.sst_rate = 'Must be between 0 and 100';
    // The share is what a co-agency or referral row IS — admin's modal won't close
    // without it. Which figure counts follows the row's basis, so the check asks
    // isShareComplete rather than looking straight at the percentage. Only a
    // co-broke row is exempt: it shares nothing by definition.
    const coAgencyAndSplitErrors = () => {
      coAgencies.forEach((c) => {
        if (isShareComplete(c)) return;
        const what = isReferral(c) ? 'Referral' : 'Commission';
        e[`ca:${c.id}`] = c.commission_basis === 'AMOUNT'
          ? `${what} amount is required`
          : `${what} % is required`;
      });
      if (!splitOk) e.split = `Splits must total 100% — currently ${Number(totalSplit.toFixed(2))}%`;
    };
    if (invoiced) {
      // Vendors and purchasers are managed on the sale's Parties tab, not here. The
      // co-agencies and the split are checked only when they changed: a figures-only
      // save must not be held up by whatever an old sale was loaded with.
      if (partyDiff.any) {
        coAgencyAndSplitErrors();
        // HQ refuses a 0% leg on an invoiced sale — remove the agent instead.
        if (!e.split && saleAgents.some((a) => !(Number(a.split_percentage) > 0))) e.split = 'Each agent needs a split above 0% — remove an agent instead';
      }
      return e;
    }
    if (vendors.length === 0) e.vendors = `At least one ${words.vendor.toLowerCase()} is required`;
    if (purchasers.length === 0) e.purchasers = `At least one ${words.purchaser.toLowerCase()} is required`;
    coAgencyAndSplitErrors();
    return e;
  };

  const allErrors = collectErrors();
  // Nothing is marked until the agent has actually tried to move on, and a wizard
  // only ever flags steps they've attempted — step 3's empty price is not an "error"
  // while they're still on step 1.
  const [checkedTo, setCheckedTo] = useState(0);
  const errors = Object.fromEntries(
    Object.entries(allErrors).filter(([k]) => stepOfField(k) <= checkedTo)
  );

  // Jump to the step holding the topmost error and bring it into view.
  const revealFirst = (keys) => {
    const first = [...keys].sort((a, b) => fieldRank(a) - fieldRank(b))[0];
    if (!first) return;
    if (wizard) setStep(stepOfField(first));
    // After the step swap has painted.
    setTimeout(() => {
      document.getElementById(`f-${first}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };

  /**
   * Arriving with ?focus=<field> from somewhere that needs this field filled in.
   *
   * Recording a stamping-fee receipt is refused until Tenancy fee collected has a
   * value, and that field lives on this screen — so the Receipts sheet links here
   * rather than leaving the agent to find it. Waits for `loaded` so the field is
   * actually mounted, and runs once.
   */
  const focusHandled = useRef(false);
  useEffect(() => {
    if (!loaded || focusHandled.current) return;
    const wanted = new URLSearchParams(location.search).get('focus');
    if (!wanted) return;
    focusHandled.current = true;
    revealFirst([wanted]);
    // The scroll itself is silent; the pulse is what says "this one".
    setTimeout(() => {
      const el = document.getElementById(`f-${wanted}`);
      if (!el) return;
      el.classList.add('field-spotlight');
      setTimeout(() => el.classList.remove('field-spotlight'), 2600);
    }, 120);
  }, [loaded, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gate a save/submit: mark everything, reveal the first problem, and stop.
  const guard = () => {
    const keys = Object.keys(allErrors);
    setCheckedTo(3);
    if (!keys.length) return true;
    revealFirst(keys);
    return false;
  };

  // Converting posts the very same payload to the booking form's own endpoint instead
  // of the plain create one — that is what links the two records (sale_id on the form,
  // an ATP_ATR attachment on the sale) and inherits the form's locked listing. Skipping
  // it would leave an unlinked sale beside a form still offering "Convert to sale".
  const create = async () => {
    const payload = buildPayload();
    const res = fromBookingForm
      ? await formsApi.createBookingFormsConvertToSaleById(fromBookingForm.id, payload)
      : await salesApi.createSales(payload);
    return res?.data ?? res;
  };
  // One action: the sale is created outright, attributed to the caller. There is no
  // draft/submit split — HQ picks it up from the Pending pile and converts it, and
  // the agent keeps editing until they do.
  const saveSale = async () => {
    if (!guard()) return;
    const token = autosave.changeToken();
    const sale = await create();
    autosave.markSaved(token);
    message.success(fromBookingForm ? 'Booking form converted to sale' : fromCoShare ? 'Sale created — co-agent added' : 'Sale created');
    // A conversion lands on the sale it produced, the way the admin portal does —
    // the agent came from a specific document and wants to see what it became. A
    // co-share sale does the same: the record it came from is now spent, and the
    // sale is the thing to look at. A plain create still goes back to the list.
    const saleId = (fromBookingForm || fromCoShare) ? (sale?.id || sale?.sale_id) : null;
    // `replace` because this screen is spent: the sale exists now, and its create
    // form is not somewhere Back should return to. Without it, backing out of the new
    // sale reopened the wizard still holding the values that made it — and saving
    // again would either duplicate the sale or fail on a form already converted.
    leave.release();
    nav(saleId ? `/sales/${saleId}` : '/sales', { replace: true });
  };
  /** Save an edit without leaving. Returns false (having shown why) when it cannot. Throws on an API error. */
  const saveEditQuietly = async () => {
    if (!guard()) return false;
    const token = autosave.changeToken();
    const body = limited ? buildLimitedPayload() : invoiced ? buildInvoicedPayload() : buildPayload();
    await salesApi.updateSalesById(editId, body);
    // The figures just saved are the new baseline: a later save on this screen
    // must not send them again unless they change again.
    if (invoiced && body.gross_sales_amount !== undefined) {
      loadedFigures.current = { gross, commPct, totalComm, vendorPayTax: !!vendorPayTax, sstRate };
    }
    autosave.markSaved(token);
    return true;
  };
  const saveEdit = async () => {
    if (partyDiff.any) { await reviewPartyChange(); return; }
    if (!(await saveEditQuietly())) return;
    message.success('Changes saved');
    leave.release();
    nav(`/sales/${editId}`);
  };

  /**
   * Save on an invoiced sale whose co-agencies / referrals or split changed.
   *
   *  1. Anything else that changed (figures, info fields) is saved first with the
   *     ordinary PUT — which refuses the parties — so HQ's check below runs on the
   *     figures the sale will actually have.
   *  2. HQ previews the party change: only the list(s) that changed, each in full.
   *  3. The "what changes" sheet shows it; Save change there sends it with a reason.
   */
  const reviewPartyChange = async () => {
    if (!guard()) return;
    let savedFirst = false;
    if (figuresChanged() || infoChanged()) {
      const body = buildInvoicedPayload();
      await salesApi.updateSalesById(editId, body);
      if (body.gross_sales_amount !== undefined) {
        loadedFigures.current = { gross, commPct, totalComm, vendorPayTax: !!vendorPayTax, sstRate };
      }
      loadedInfo.current = JSON.stringify(buildLimitedPayload());
      savedFirst = true;
      // The rest is on the sale now, but the party change still lives only here.
      // Re-stamp the device copy so it stays newer than the sale it just updated —
      // an older copy is dropped on the next open as stale.
      autosave.noteChange();
    }
    const body = partyChangeBody(partyDiff, coAgencies, saleAgents);
    try {
      const r = await salesApi.previewParties(editId, body);
      setPartyReview({ preview: r?.data ?? r, body, savedFirst });
    } catch (e) {
      if (!savedFirst) throw e;
      message.error(e.message);
      message.info(PARTY_CHANGE_NOT_SAVED);
    }
  };
  const confirmPartyChange = async (reason) => {
    const token = autosave.changeToken();
    await salesApi.changeParties(editId, { ...partyReview.body, reason });
    autosave.markSaved(token);
    message.success(partyChangeSavedText(partyReview.preview));
    setPartyReview(null);
    leave.release();
    nav(`/sales/${editId}`);
  };
  const cancelPartyChange = () => {
    const savedFirst = partyReview?.savedFirst;
    setPartyReview(null);
    if (savedFirst) message.info(PARTY_CHANGE_NOT_SAVED);
  };

  // Wizard: a step only has to clear ITS OWN fields to advance — the full set is
  // checked again on submit. Failing marks this step's fields and stays put.
  const goNext = () => {
    const mine = Object.keys(allErrors).filter((k) => stepOfField(k) === step);
    setCheckedTo((c) => Math.max(c, step));
    if (mine.length) { revealFirst(mine); return; }
    setStep((s) => Math.min(3, s + 1));
  };

  /* ── device copy + leave guard ─────────────────────────────────────────────
     The owner's "A" for the sale wizard (2026-10-02): a sale has no private
     draft on the server, so what is typed is kept on this device only, a moment
     after each change, and brought back after a reload — on the step it was on.
     The same applies to /sales/:id/edit: an edit reaches the server only through
     Save changes. Same keys as the desktop SaleCreate, so rotating an iPad across
     the 1100px switch brings the typing across. */
  const [loadedTick, setLoadedTick] = useState(0);
  useEffect(() => { if (settled) setLoadedTick((t) => t + 1); }, [settled]);
  const startedFrom = fromBookingForm?.id ? fromBookingForm.id
    : fromCoShare?.id ? `co-share:${fromCoShare.id}`
      : fromProject?.project_id ? `project:${fromProject.project_id}${fromProject.unit_id ? `:${fromProject.unit_id}` : ''}`
        : null;
  // The project row and its units are re-fetched rather than kept: the unit list
  // runs to hundreds of rows, and both are the server's to describe.
  const loadProject = (pid) => {
    setProject(null); setProjectOpts([]); setUnitOpts([]); setUnitsLoaded(false);
    if (!pid) return;
    projectsApi.getProjectById(pid)
      .then((pr) => {
        const proj = pr?.data ?? pr ?? null;
        setProject(proj);
        if (proj?.id) setProjectOpts([{ value: proj.id, label: proj.name, data: proj }]);
      })
      .catch(() => {});
    fetchUnits(pid);
  };
  const autosave = useFormAutosave({
    draftKey: `sale:${editId || `new${startedFrom ? `:from:${startedFrom}` : ''}`}`,
    userId: me?.id,
    readValues: () => ({
      dealType, saleDate, propertyAddress, gross, commPct, totalComm, vendorPayTax, sstRate,
      vendors, purchasers, coAgencies, saleAgents,
      listingId, listing, propertyType, builtUpArea, builtUpType, projectId, unitId,
      tenancyPeriod, tenancyFrom, tenancyTo, tenancyFee, tenancyDuty, tenancyPaidBy, tenancyDutyAgent,
      otherTerms, loDate, loBank, spaDate, remarks, bookingFormNo,
    }),
    readMeta: () => ({ step, checkedTo, lastEdit: lastEdit.current }),
    writeValues: (v, meta) => {
      setDealType(v.dealType || 'SUBSALES');
      setSaleDate(v.saleDate ?? null);
      setPropertyAddress(v.propertyAddress ?? '');
      setGross(v.gross ?? '');
      setCommPct(v.commPct ?? '');
      setTotalComm(v.totalComm ?? '');
      setVendorPayTax(v.vendorPayTax !== false);
      setSstRate(v.sstRate ?? 8);
      setVendors(v.vendors || []);
      setPurchasers(v.purchasers || []);
      setCoAgencies(v.coAgencies || []);
      if (v.saleAgents?.length) setSaleAgents(v.saleAgents);
      setListingId(v.listingId || null);
      setListing(v.listing || null);
      setPropertyType(v.propertyType || undefined);
      setBuiltUpArea(v.builtUpArea ?? '');
      setBuiltUpType(v.builtUpType ?? '');
      setProjectId(v.projectId || null);
      setUnitId(v.unitId || null);
      loadProject(v.projectId);
      setTenancyPeriod(v.tenancyPeriod ?? '');
      setTenancyFrom(v.tenancyFrom ?? null);
      setTenancyTo(v.tenancyTo ?? null);
      setTenancyFee(v.tenancyFee ?? '');
      setTenancyDuty(v.tenancyDuty ?? '');
      setTenancyPaidBy(v.tenancyPaidBy ?? null);
      setTenancyDutyAgent(v.tenancyDutyAgent ?? null);
      setOtherTerms(v.otherTerms ?? '');
      setLoDate(v.loDate ?? null);
      setLoBank(v.loBank ?? '');
      setSpaDate(v.spaDate ?? null);
      setRemarks(v.remarks ?? '');
      setBookingFormNo(v.bookingFormNo ?? '');
      if (meta.lastEdit) lastEdit.current = meta.lastEdit;
      if (meta.checkedTo != null) setCheckedTo(meta.checkedTo);
      if (wizard && meta.step) setStep(meta.step);
    },
    serverUpdatedAt,
    loadedTick,
  });
  // Moving between steps is not an edit, but once there is typing to keep, the
  // copy should reopen on the step the agent was on.
  useEffect(() => { if (autosave.dirty) autosave.noteChange(); }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new sale has no draft to save on the way out — creating it is the last
  // step's deliberate act — so leaving one offers Discard only. An edit can save.
  const leave = useUnsavedChanges(autosave.dirty, {
    what: editing ? 'this sale' : fromBookingForm ? 'this conversion' : 'this new sale',
    keptOnDevice: true,
    // A co-agency / split change on an invoiced sale cannot be saved on the way
    // out: it needs HQ's "what changes" check and a reason. So no "Save & leave"
    // then — the guard offers Keep editing (then Save changes) or Discard.
    ...(editing && !partyDiff.any ? {
      onSave: async () => {
        try {
          return await saveEditQuietly();
        } catch (e) {
          message.error(e.message);
          return false;
        }
      },
      saveLabel: 'Save changes & leave',
    } : {}),
    onDiscard: autosave.discardDevice,
  });

  const myTier = Number(me?.gci_tier) || null;
  const mySplit = saleAgents.find((a) => a.self)?.split_percentage;
  // The estimate runs on what CID actually earns, not on the deal. On a "They
  // list" sale the commission above is the whole fee the OTHER agency collects,
  // so quoting a tier of it would promise the agent roughly double their pay.
  const myEstBase = cidGciBase(coAgencies, netGci);
  const myShareEst = myTier ? myEstBase * (myTier / 100) * ((Number(mySplit) || 100) / 100) : 0;
  const STEP_SUB = { 1: 'Step 1 of 3 · Property', 2: 'Step 2 of 3 · Parties', 3: 'Step 3 of 3 · Price & commission' };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        back
        title={editing ? 'Edit sale' : fromBookingForm ? 'Convert to sale' : fromCoShare ? 'Sale from co-share' : 'New sale'}
        sub={editing ? 'update, then save changes'
          : fromBookingForm && step === 1 ? `Prefilled from ${fromBookingForm.form_ref}`
            : fromCoShare && step === 1 ? `Co-share with ${fromCoShare.requester?.name || 'another agent'}`
              : STEP_SUB[step]}
        right={wizard ? <StepDots step={step} /> : null}
      />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {editing && !loaded ? <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</div> : (<>
        <RestoredDraftBanner
          autosave={autosave}
          onServer={editing}
          notYet={fromBookingForm ? 'Nothing is converted until you press Convert to sale.' : 'Nothing reaches HQ until you press Create sale.'}
        />
        {/* Converted sales: financials/parties are HQ-locked, info fields stay editable */}
        {limited && (
          <div style={{ background: 'color-mix(in srgb,var(--warn) 9%,var(--surface))', border: '1px solid color-mix(in srgb,var(--warn) 30%,var(--border))', borderRadius: 12, padding: '14px 15px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            This sale is <b style={{ color: 'var(--text)' }}>Converted</b> — deal type, financials, parties and splits are locked by HQ.
            You can still update property info, dates, remarks and your booking form no.
          </div>
        )}
        {/* Invoiced (Converted) sales: the figures — and, through HQ's check, the
            co-agencies, referrals and splits — are still open until Completed */}
        {invoiced && (
          <div style={{ background: 'color-mix(in srgb,var(--warn) 9%,var(--surface))', border: '1px solid color-mix(in srgb,var(--warn) 30%,var(--border))', borderRadius: 12, padding: '14px 15px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Invoiced — still editable</div>
            {commissionHandSet
              ? 'The invoice is already out. You can still correct the figures and HQ finance is told, but HQ finance has set your commission by hand — changing the figures will not change it.'
              : 'The invoice is already out. You can still correct the figures — HQ finance is told, and your commission is recalculated.'}
            {partiesOpen
              ? ' Co-agencies, referrals and agent splits can still change too — you see what the change does before it is saved. Deal type and property link stay locked.'
              : ' Deal type, property link, parties and splits stay locked.'}
          </div>
        )}
        {/* Raised from an accepted co-share. Says what was carried over and what is
            fixed, so the agent isn't left wondering why the split arrived pre-filled
            with someone else on it. */}
        {fromCoShare && (
          <div data-tour="sale-cs-banner" style={{ background: 'color-mix(in srgb,var(--ok) 9%,var(--surface))', border: '1px solid color-mix(in srgb,var(--ok) 30%,var(--border))', borderRadius: 12, padding: '14px 15px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.5 }}>
            Co-share with <b style={{ color: 'var(--text)' }}>{fromCoShare.requester?.name || 'another agent'}</b>
            {fromCoShare.agreed_split_percentage != null ? ` at ${fromCoShare.agreed_split_percentage}%` : ''}.
            They are already in the agent split below and stay on this sale — the percentages are still yours to adjust.
          </div>
        )}
        {(!wizard || step === 1) && (<>
        {/* Deal */}
        <div style={card}>
          {!structureLocked && (() => {
            /* Two ways this arrives already decided, and in both the server refuses a
               sale that disagrees — so it is locked rather than left open to be bounced
               on save, and changing it would wipe the prefill either way.

               · from a booking form — the form's own sale_type decides it
               · from a co-share    — the agreed listing decides it: a RENT listing can
                 only become a RENT deal, and switching to PROJECT would drop the
                 listing link that the whole co-share is about. */
            const lockedDeal = !!fromBookingForm || !!fromCoShare;
            const dealHint = fromBookingForm
              ? `Set by ${fromBookingForm.form_ref} and cannot be changed during conversion.`
              : fromCoShare
                ? `Set by the co-shared listing${fromCoShare.listing_type === 'RENT' ? ' — a rental listing makes a Rent deal' : ''}, and cannot be changed here.`
                : undefined;
            return (
              <Field req label="Deal type" hint={dealHint}>
                <Segmented block value={dealType} onChange={onDealType} disabled={lockedDeal}
                  options={[{ label: 'Subsales', value: 'SUBSALES' }, { label: 'Rent', value: 'RENT' }, { label: 'Project', value: 'PROJECT' }, { label: 'Auction', value: 'AUCTION' }]} />
              </Field>
            );
          })()}
          <Field req name="sale_date" label="Sale date" error={errors.sale_date}>
            <SheetDate title="Sale date" placeholder="Pick the sale date" value={saleDate} onChange={byUser(setSaleDate)} allowClear={false}
              style={errorBorder(errors.sale_date)} />
          </Field>
          {/* Sits with the deal's identifiers because that is what it is, and above
              the property because it is the first thing on the pad being copied in.
              "Your" is load-bearing: this app already calls the OTP/OTR it issues a
              booking form, and this is the other one. */}
          <Field label="Your booking form no. (optional)"
            hint="From your own booking form pad — not the OTP/OTR CID issues. Fill it in if you track your cases by it.">
            <Input value={bookingFormNo} onChange={(e) => byUser(setBookingFormNo)(e.target.value)} maxLength={100}
              placeholder="e.g. 001234" />
          </Field>
          {!isProject && !structureLocked && (() => {
            // The listing was locked under offer when the form was issued, so the sale
            // inherits it and the server refuses a swap. Same rule as the deal type, and
            // the same lock the admin sale form applies during a conversion.
            //
            // A co-share sale is locked the same way and for the same reason: the
            // property is the one the two agents agreed on, and the server refuses a
            // sale from that request pointed anywhere else.
            const lockedListing = !!fromBookingForm?.listing_id || !!fromCoShare?.listing_id;
            const lockHint = fromBookingForm?.listing_id
              ? `Carried over from ${fromBookingForm.form_ref} and cannot be changed during conversion.`
              : fromCoShare?.listing_id
                ? `The property this co-share with ${fromCoShare.requester?.name || 'the other agent'} was agreed on. It cannot be changed here.`
                : undefined;
            return (
              <Field
                label={lockedListing ? 'Linked listing' : 'Link to a listing (optional)'}
                hint={lockHint}
              >
                <ListingLinkField
                  value={listingId}
                  listing={listing}
                  listingType={LISTING_TYPE_BY_DEAL[dealType]}
                  status="ACTIVE"
                  linkable
                  onChange={onPickListing}
                  disabled={lockedListing}
                  placeholder="Browse listings — fills the property details"
                  title="Link a listing"
                  sub="fills the property details below"
                />
              </Field>
            );
          })()}
        </div>

        {/* Project (PROJECT deal type only) */}
        {isProject && !structureLocked && (
          <>
            <div style={sectionLabel}>PROJECT</div>
            <div style={{ ...card, marginTop: -4 }}>
              <Field req name="project" label="Project" error={errors.project}>
                <SheetSelect
                  style={errorBorder(errors.project)}
                  title="Pick a project"
                  placeholder="Search projects…"
                  searchPlaceholder="Search project name…"
                  value={projectId}
                  selectedLabel={project?.name}
                  allowClear
                  onSearch={searchProjects}
                  onChange={onPickProject}
                  options={projectOpts}
                  onLoadMore={loadMoreProjects}
                  hasMore={projectHasMore}
                  loadingMore={projectLoadingMore}
                  notFoundContent="No projects match that name."
                />
              </Field>
              {/* The unit is optional: most projects have no (or not all) units
                  uploaded, and one that is not in the list is typed into the address. */}
              {projectId && (unitsLoaded && unitOpts.length === 0 && !unitId ? (
                <div style={{ fontSize: 12.5, color: 'var(--text)', marginBottom: 10, lineHeight: 1.5 }}>
                  No units uploaded for this project — type the unit / lot number in the property address.
                </div>
              ) : (
                <Field name="unit" label="Unit (optional)" error={errors.unit}
                  hint="Unit not in the list? Leave this empty and type it in the property address.">
                  <SheetSelect
                    style={errorBorder(errors.unit)}
                    title="Pick a unit"
                    placeholder="Select a unit — fills the price"
                    searchPlaceholder="Search unit no or type…"
                    value={unitId}
                    allowClear
                    showSearch
                    onChange={onPickUnit}
                    options={unitOpts}
                    notFoundContent="No unit matches. Type it in the property address instead."
                  />
                </Field>
              ))}
              <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Commission &amp; SST come from the project. A picked unit also fills the price and built-up — only AVAILABLE units can be picked.</div>
            </div>
          </>
        )}

        {/* Property */}
        <div style={sectionLabel}>PROPERTY</div>
        <div style={{ ...card, marginTop: -4 }}>
          <Field req name="property_address" label="Property address" error={errors.property_address}
            hint={linkedToListing ? 'Auto-filled from linked listing' : isProject && !unitId && !structureLocked ? 'Include the unit / lot number' : undefined}>
            <Input.TextArea status={errors.property_address ? 'error' : undefined} value={propertyAddress} onChange={(e) => byUser(setPropertyAddress)(e.target.value)} rows={2}
              placeholder={isProject && !unitId ? `e.g. Lot 12, ${project?.name || 'the project'}` : 'Property address'} disabled={linkedToListing} />
          </Field>
          <Field label="Property type" hint={linkedToListing ? 'From listing' : undefined}>
            <SheetSelect title="Property type" value={propertyType} onChange={byUser(setPropertyType)} options={PROPERTY_TYPE_OPTIONS}
              placeholder="Select property type" allowClear showSearch disabled={linkedToListing} />
          </Field>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><Field label="Built-up area (sqft)" hint={linkedToListing ? 'From listing' : undefined}><Input value={builtUpArea} onChange={(e) => byUser(setBuiltUpArea)(e.target.value)} placeholder="e.g. 1200" disabled={linkedToListing} /></Field></div>
            <div style={{ flex: 1 }}><Field label="Built-up type"><Input value={builtUpType} onChange={(e) => byUser(setBuiltUpType)(e.target.value)} placeholder="e.g. Intermediate, Corner" /></Field></div>
          </div>
        </div>
        </>)}

        {(!wizard || step === 3) && !limited && (<>
        {/* Financial */}
        <div style={sectionLabel}>FINANCIAL</div>
        <div style={{ ...card, marginTop: -4 }}>
          {isProject && !unitId && !invoiced && (
            <div style={{ background: 'color-mix(in srgb,var(--primary) 8%,var(--surface))', border: '1px solid color-mix(in srgb,var(--primary) 25%,var(--border))', borderRadius: 10, padding: '14px 15px', fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.5 }}>
              {projectId
                ? 'No unit picked: type the selling price — the commission and SST come from the project.'
                : 'Select a project above to fill in its commission and SST.'}
            </div>
          )}
          <Field
            req
            name="gross"
            error={errors.gross}
            label={isRent ? 'Rental amount' : 'Gross sales amount'}
            hint={priceFromUnit ? 'From unit net price' : isRent ? 'Monthly rental amount' : 'Property transaction price'}
          >
            <MoneyInput value={gross} onChange={onGross} placeholder="0.00" disabled={priceFromUnit} status={errors.gross ? 'error' : undefined} />
          </Field>

          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Field req={!fromUnit} name="comm_pct" label="Commission rate" error={errors.comm_pct}>
                <Input data-tour="sale-comm-pct" type="number" min={0} value={commPct} onChange={(e) => onPct(e.target.value)} onBlur={normalizePct}
                  status={errors.comm_pct ? 'error' : undefined}
                  suffix="%" placeholder="0.00" disabled={fromUnit} />
              </Field>
            </div>
            <div style={{ flex: 1 }}>
              <Field req name="total_comm" label="Total commission" error={errors.total_comm}>
                <MoneyInput value={totalComm} onChange={onTotal} placeholder="0.00" disabled={fromUnit} status={errors.total_comm ? 'error' : undefined} />
              </Field>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -4 }}>
            {fromUnit ? 'From project config' : 'Edit either rate or total — the other recalculates'}
          </div>

          <div style={subLabel}>SST CALCULATION</div>
          <Field label="SST bearer" hint={fromUnit ? 'From project setup' : 'Who bears the SST cost?'}>
            <SheetSelect
              title="SST bearer"
              value={vendorPayTax ? 'VENDOR' : 'CID'}
              onChange={(v) => byUser(setVendorPayTax)(v === 'VENDOR')}
              disabled={fromUnit}
              options={[
                { value: 'VENDOR', label: `${words.vendor} pays (billed on top)` },
                { value: 'CID', label: 'Agency absorbs (deducted)' },
              ]}
            />
          </Field>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <Field req name="sst_rate" label="SST rate" error={errors.sst_rate}>
                <Input type="number" min={0} max={100} value={sstRate} onChange={(e) => byUser(setSstRate)(e.target.value)}
                  status={errors.sst_rate ? 'error' : undefined}
                  onBlur={normalizeSstRate} suffix="%" disabled={fromUnit} />
              </Field>
            </div>
            <div style={{ flex: 1 }}>
              <Field label="SST amount"><MoneyInput value={money2(sstAmount)} disabled /></Field>
            </div>
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -4 }}>Auto-calculated from commission &amp; SST rate</div>

          <div style={subLabel}>NET AMOUNT</div>
          <div style={{ background: 'color-mix(in srgb,var(--primary) 5%,transparent)', border: '1px solid color-mix(in srgb,var(--primary) 18%,var(--border))', borderRadius: 10, padding: '10px 11px' }}>
            <Field
              label="Net GCI"
              hint={myEstBase < netGci - 0.005
                ? 'Auto-calculated · The whole deal — the other agency collects it'
                : 'Auto-calculated · Base for all commission splits'}
            >
              <MoneyInput value={money2(netGci)} disabled />
            </Field>
            {/* "They list" — the fee above is what the other agency banks. What
                every split actually runs on is CID's portion. */}
            {myEstBase < netGci - 0.005 && (
              <div style={{ marginTop: 10 }}>
                <Field label="CID&rsquo;s portion" hint="They list · Base for all commission splits">
                  <MoneyInput value={money2(myEstBase)} disabled />
                </Field>
              </div>
            )}
          </div>

          {isRent && (
            <>
              <div style={subLabel}>TENANCY FEE</div>
              <TenancyFeeFields
                fee={tenancyFee} setFee={byUser(setTenancyFee)}
                duty={tenancyDuty} setDuty={byUser(setTenancyDuty)}
                paidBy={tenancyPaidBy} setPaidBy={byUser(setTenancyPaidBy)}
                dutyAgent={tenancyDutyAgent} setDutyAgent={byUser(setTenancyDutyAgent)} saleAgents={saleAgents}
                locked={tenancyLocked}
                monthlyRent={gross} years={tenancyPeriod}
              />
            </>
          )}
        </div>
        {wizard && netGci > 0 && myTier && (
          <div style={{ background: 'var(--chip)', borderRadius: 14, padding: '15px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500 }}>Estimated my share @ {myTier}%</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--primary)' }}>{RM(myShareEst)}</span>
          </div>
        )}
        </>)}

        {(!wizard || step === 2) && (!structureLocked || partiesOpen) && (<>
        {/* Parties. On an invoiced sale only the co-agencies / referrals and the
            split are here — vendors and purchasers stay on the sale's Parties tab. */}
        <div style={sectionLabel}>{invoiced ? 'CO-AGENCIES & REFERRALS' : 'PARTIES'}</div>
        <div style={{ ...card, marginTop: -4 }}>
          {!invoiced && (<>
          <ContactMultiPicker tour="sale-vendor" type="VENDOR" req name="vendors" error={errors.vendors} label={words.vendors} picked={vendors} noun={words.vendor.toLowerCase()} hint={TEAM_SCOPE_HINT}
            onCreateNew={() => setQuick({ type: 'vendor', sub: words.vendorType })}
            onAdd={(d) => byUser(setVendors)((xs) => xs.some((v) => v.id === d.id) ? xs : [...xs, d])} onRemove={(id) => byUser(setVendors)((xs) => xs.filter((v) => v.id !== id))} />
          <ContactMultiPicker tour="sale-purchaser" type="PURCHASER" req name="purchasers" error={errors.purchasers} label={words.purchasers} max={4} picked={purchasers} noun={words.purchaser.toLowerCase()} hint={TEAM_SCOPE_HINT}
            onCreateNew={() => setQuick({ type: 'purchaser', sub: words.purchaserType })}
            onAdd={(d) => byUser(setPurchasers)((xs) => xs.length >= 4 || xs.some((p) => p.id === d.id) ? xs : [...xs, d])} onRemove={(id) => byUser(setPurchasers)((xs) => xs.filter((p) => p.id !== id))} />
          </>)}

          <Field
            label={(
              <>
                Co-agencies / referrals
                {coAgencies.length > 0 && (
                  <span style={{ color: 'var(--primary)', fontWeight: 700 }}> · {coAgencies.length} added</span>
                )}
              </>
            )}
          >
            <SheetSelect
              title="Add a co-agency"
              placeholder="Search & add a co-agency…"
              searchPlaceholder="Search co-agencies & referrals…"
              value={null}
              onSearch={caLookup.search}
              onChange={addCoAgency}
              options={[createNewOption('co-agency'), ...markPicked(caLookup.options, caIds)]}
              onLoadMore={caLookup.loadMore}
              hasMore={caLookup.hasMore}
              loadingMore={caLookup.loadingMore}
              notFoundContent="No co-agency matches — use ＋ Create new above."
            />
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{CO_AGENCY_SCOPE_HINT}</div>
            {coAgencies.map((c) => (
              <CoAgencyRow
                key={c.id}
                row={c}
                error={errors[`ca:${c.id}`]}
                onPatch={(patch) => patchCoAgency(c.id, patch)}
                onRole={(role) => setCoRole(c.id, role)}
                onLinkLetter={(letterId, lt) => linkLetter(c.id, letterId, lt)}
                onRemove={() => removeCoAgency(c.id)}
                netGci={netGci}
                rows={coAgencies}
              />
            ))}
          </Field>
        </div>

        {/* Agents & commission split */}
        <div style={sectionLabel}>AGENTS &amp; COMMISSION SPLIT</div>
        {/* A bad split is a property of the whole list, not one row, so the card
            itself carries the mark — the same thing admin does by turning its Agents
            party card red. */}
        <div id="f-split" data-tour="sale-split" style={{ ...card, marginTop: -4, scrollMarginTop: 90, ...(errors.split ? { borderColor: 'var(--bad)' } : null) }}>
          {saleAgents.map((a) => (
            <div key={a.agent_id} style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
              <div style={{ fontSize: 13, fontWeight: 600, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {a.name}{a.self ? ' (you)' : ''}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 'none' }}>
                {saleAgents.length > 1
                  ? <Input type="number" min={0} max={100} value={a.split_percentage} onChange={(e) => patchAgentSplit(a.agent_id, e.target.value)} suffix="%" style={{ width: 96 }} />
                  : <span style={{ fontSize: 13, fontWeight: 600 }}>100%</span>}
                {/* The co-share agent cannot be taken off: an accepted co-share is
                    what this sale is being raised from, and the server refuses a
                    split without them. Their percentage stays editable. */}
                {!a.self && !a.coShare && <span onClick={() => removeAgent(a.agent_id)} style={{ cursor: 'pointer', color: 'var(--bad)', fontSize: 12.5 }}>Remove</span>}
                {a.coShare && <span style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600 }}>co-share</span>}
              </div>
            </div>
          ))}
          <SheetSelect
            title="Add an agent to the split"
            placeholder="＋ Add another agent to split commission…"
            searchPlaceholder="Search name or REN no…"
            value={null}
            onSearch={agentLookup.search}
            onChange={addAgent}
            options={markPicked(agentLookup.options, agentIds)}
            onLoadMore={agentLookup.loadMore}
            hasMore={agentLookup.hasMore}
            loadingMore={agentLookup.loadingMore}
            notFoundContent="No agent matches that name or REN."
          />
          {(saleAgents.length > 1 || errors.split) && (
            <div style={{ fontSize: 12.5, fontWeight: 600, color: splitOk ? 'var(--muted)' : 'var(--bad)' }}>
              Total split: {Number(totalSplit.toFixed(2))}%{splitOk ? ' ✓' : ' — must be 100%'}
            </div>
          )}
          {errors.split && splitOk && <div style={{ fontSize: 11.5, color: 'var(--bad)', fontWeight: 600 }}>{errors.split}</div>}
          {partiesOpen && (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>
              You stay on the split — only HQ can take you off it. A change here, or to the
              co-agencies above, is checked by HQ when you save: you see what it does to the
              commission before it is saved.
            </div>
          )}
        </div>
        </>)}

        {(!wizard || step === 3) && (<>
        {/* Others */}
        <div style={sectionLabel}>OTHERS</div>
        <div style={{ ...card, marginTop: -4 }}>
          {isRent ? (
            <>
              <Field label="Tenancy period (years)"><Input type="number" min={0} step={0.5} value={tenancyPeriod} onChange={(e) => onTenancyPeriod(e.target.value)} placeholder="e.g. 2" /></Field>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}><Field label="Tenancy from"><SheetDate title="Tenancy from" placeholder="Start date" value={tenancyFrom} onChange={onTenancyFrom} /></Field></div>
                <div style={{ flex: 1 }}>
                  <Field label="Tenancy to" hint="Fills in from period & start date">
                    <SheetDate title="Tenancy to" placeholder="End date" value={tenancyTo} onChange={byUser(setTenancyTo)}
                      footerHint="Derived from the period and start date — pick a day here only to override it." />
                  </Field>
                </div>
              </div>
              {limited && (
                // These live in the Financial card's TENANCY FEE block (where admin
                // keeps them), but a Converted sale hides that whole card — and this
                // is exactly when they get filled in, because the stamping happens
                // long after the deal closes and the agent is the one at the LHDN
                // counter. So they surface here only when their usual home is off
                // screen; the two never both render.
                <>
                  <div style={subLabel}>TENANCY FEE</div>
                  <TenancyFeeFields
                    fee={tenancyFee} setFee={byUser(setTenancyFee)}
                    duty={tenancyDuty} setDuty={byUser(setTenancyDuty)}
                    paidBy={tenancyPaidBy} setPaidBy={byUser(setTenancyPaidBy)}
                dutyAgent={tenancyDutyAgent} setDutyAgent={byUser(setTenancyDutyAgent)} saleAgents={saleAgents}
                    locked={tenancyLocked}
                    monthlyRent={gross} years={tenancyPeriod}
                  />
                </>
              )}
              <Field label="Other terms and conditions">
                <Input.TextArea value={otherTerms} onChange={(e) => byUser(setOtherTerms)(e.target.value)} rows={3} placeholder="Other terms and conditions" />
              </Field>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <Field label="LO date" hint="Letter of Offer date">
                    <SheetDate title="LO date" placeholder="Letter of Offer date" value={loDate} onChange={byUser(setLoDate)} />
                  </Field>
                </div>
                <div style={{ flex: 1 }}><Field label="SPA date"><SheetDate title="SPA date" placeholder="SPA date" value={spaDate} onChange={byUser(setSpaDate)} /></Field></div>
              </div>
              <Field label="LO bank"><Input value={loBank} onChange={(e) => byUser(setLoBank)(e.target.value)} placeholder="Financing bank" /></Field>
            </>
          )}
          <Field label="Remark"><Input.TextArea value={remarks} onChange={(e) => byUser(setRemarks)(e.target.value)} rows={3} placeholder="Additional remarks" /></Field>
        </div>
        </>)}
        </>)}
      </div>

      <PartyQuickCreate open={!!quick} type={quick?.type} defaultSub={quick?.sub} onClose={() => setQuick(null)} onCreated={onQuickCreated} />

      <PartyChangeSheet
        open={!!partyReview}
        preview={partyReview?.preview}
        meId={me?.id}
        onClose={cancelPartyChange}
        onConfirm={confirmPartyChange}
        onError={(e) => message.error(e.message)}
      />

      <div style={{ padding: '10px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <AutosaveStatus
          autosave={autosave}
          idleText={editing ? 'Changes reach the sale when you press Save changes' : 'Nothing reaches HQ until you create the sale'}
          style={{ textAlign: 'center' }}
        />
        <div style={{ display: 'flex', gap: 10 }}>
          {editing ? (
            <AsyncButton type="primary" block onClick={saveEdit} onError={(e) => message.error(e.message)}>Save changes</AsyncButton>
          ) : (
            <>
              {step > 1 && <Button size="large" onClick={() => setStep((s) => Math.max(1, s - 1))} style={{ height: 46 }}>Back</Button>}
              {step === 1 && <Button data-tour="sale-next" type="primary" size="large" block style={{ height: 46 }} onClick={goNext}>Next · Parties</Button>}
              {step === 2 && <Button data-tour="sale-next" type="primary" size="large" block style={{ height: 46 }} onClick={goNext}>Next · Price</Button>}
              {step === 3 && (
                <AsyncButton data-tour="sale-next" type="primary" block onClick={saveSale} onError={(e) => message.error(e.message)}>
                  {fromBookingForm ? 'Convert to sale' : 'Create sale'}
                </AsyncButton>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Reusable typeahead → chip list, backed by the unified contacts lookup (scoped to the
// caller's own contact book, with a pinned "+ Create new…" quick-create entry). Paged,
// and already-picked ids ride along as include_ids so a party HQ attached from outside
// the caller's scope still resolves in the list it is selected in.
function ContactMultiPicker({ type, label, req, max, picked, onAdd, onRemove, onCreateNew, noun, hint, name, error, tour }) {
  const pickedIds = picked.map((p) => p.id);
  const lookup = usePagedOptions(async (q, page) => {
    const r = await contactsApi.listContactsLookup({
      type, q, page, limit: CONTACT_PAGE, include_ids: pickedIds.join(',') || undefined,
    });
    // `tour` rides along so Learn Mode can spotlight a row INSIDE the picker
    // sheet — the sheet renders below the tour's blockers, so a step that opens
    // one is only finishable if the next step can anchor an option in it.
    return (r?.data ?? r ?? []).map((c) => ({ value: c.id, label: contactLabel(c), sub: contactSub(c), data: c, tour: c.tour }));
  });
  const atMax = max && picked.length >= max;
  const opts = markPicked(lookup.options, pickedIds);
  return (
    <Field
      req={req}
      name={name}
      error={error}
      label={(
        <>
          {label}{max ? ` (max ${max})` : ''}
          {picked.length > 0 && (
            <span style={{ color: 'var(--primary)', fontWeight: 700 }}> · {picked.length} added</span>
          )}
        </>
      )}
    >
      <SheetSelect
        tour={tour}
        style={errorBorder(error)}
        title={`Add a ${noun || 'contact'}`}
        placeholder={atMax ? `Max ${max} reached` : 'Search & add…'}
        searchPlaceholder={`Search ${noun || 'contact'}s…`}
        disabled={atMax}
        value={null}
        onSearch={lookup.search}
        onChange={(id) => {
          if (id === CREATE_NEW) { onCreateNew?.(); return; }
          const d = lookup.options.find((o) => o.value === id)?.data;
          if (d) onAdd(d);
        }}
        options={onCreateNew ? [createNewOption(noun || 'contact'), ...opts] : opts}
        onLoadMore={lookup.loadMore}
        hasMore={lookup.hasMore}
        loadingMore={lookup.loadingMore}
        notFoundContent={`No ${noun || 'contact'} matches — use ＋ Create new above.`}
      />
      {/* The scope note explains the SEARCH, so it stays with the search box — above
          the parties that have been picked, never sandwiched between them. */}
      {hint && <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{hint}</div>}
      {picked.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 2 }}>
          {picked.map((p, i) => (
            <PickedParty key={p.id} index={i + 1} name={p.name} sub={contactSub(p)} onRemove={() => onRemove(p.id)} />
          ))}
        </div>
      )}
    </Field>
  );
}

/**
 * A party that IS on the sale.
 *
 * It used to be a pale chip with a tiny ✕, which sat lighter on the page than the
 * empty "Search & add…" box above it — so a filled field read as an empty one, and
 * the only way off was a 12px tap target. This is a solid row instead: numbered like
 * the admin portal's party table, the identifier that tells two same-named people
 * apart underneath, and a Remove control big enough to hit on purpose.
 */
function PickedParty({ index, name, sub, onRemove }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      background: 'var(--surface2)', border: '1px solid var(--border)',
      borderRadius: 10, padding: '9px 11px',
    }}>
      <span style={{
        width: 20, height: 20, borderRadius: 6, flex: 'none',
        background: 'var(--chip)', color: 'var(--primary)',
        fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{index}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
        {sub && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>}
      </div>
      <span
        role="button"
        tabIndex={0}
        onClick={onRemove}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRemove(); } }}
        style={{ flex: 'none', color: 'var(--bad)', fontSize: 12.5, fontWeight: 600, padding: '6px 2px', cursor: 'pointer' }}
      >
        Remove
      </span>
    </div>
  );
}

/**
 * One co-agency / referral row.
 *
 * The two are the same table but bill differently, so the row shape follows the type
 * rather than offering controls that don't apply:
 *   CO_AGENCY — "We list / They list", plus the figure each of those needs.
 *               "We list": CID collects the fee and pays them a share, so the row
 *               states THEIR cut and a verified letter can fill it in.
 *               "They list": they collect and pay CID, so the row states CID's
 *               PORTION of the deal instead — the commission on the sale is the
 *               whole deal, and that portion is what the agent split, the HQ
 *               royalty, GCI and the invoice to them all run on.
 *   REFERRAL  — a rate and nothing else. commission.service ignores `role` for a
 *               referral and always deducts, so showing a co-broke toggle here only
 *               offers a way to zero a fee that is still owed.
 */
function CoAgencyRow({ row, onPatch, onRole, onLinkLetter, onRemove, error, netGci, rows }) {
  const referral = isReferral(row);
  const cobroke = row.role === 'COBROKE';
  const flat = row.commission_basis === 'AMOUNT';
  const cidFlat = row.cid_share_basis === 'AMOUNT';
  const setCidBasis = (basis) => onPatch(basis === 'AMOUNT'
    ? { cid_share_basis: 'AMOUNT', cid_share_percentage: '0', cid_share_amount: '' }
    : { cid_share_basis: 'PERCENTAGE', cid_share_percentage: '', cid_share_amount: '' });
  const split = cobroke ? dealSplitSummary(row, netGci) : null;
  // Switching basis clears the figure that no longer applies, so the row can never
  // hold two numbers and leave which one is the agreement to guess.
  const setBasis = (basis) => onPatch(basis === 'AMOUNT'
    ? { commission_basis: 'AMOUNT', commission_percentage: '0', commission_amount: '', referral_base: 'CID_PORTION' }
    : { commission_basis: 'PERCENTAGE', commission_percentage: '', commission_amount: '', referral_base: 'CID_PORTION' });
  // A percentage referral can take its % of what CID keeps after the "We list"
  // co-agencies are paid, rather than of CID's whole portion — see
  // lib/coAgencyShare.referralBaseOf. The preview prices it against the other rows.
  const baseSummary = referral && !flat ? referralBaseSummary(row, rows, netGci) : null;
  return (
    <div
      id={`f-ca:${row.id}`}
      style={{
        border: `1px solid ${error ? 'var(--bad)' : 'var(--border)'}`,
        borderRadius: 10, padding: 10, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10,
        scrollMarginTop: 90,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</span>
          <Chip label={referral ? 'Referral' : 'Co-agency'} tone={referral ? 'purple' : 'brand'} />
        </div>
        <span onClick={onRemove} style={{ cursor: 'pointer', color: 'var(--bad)', fontSize: 12.5, flex: 'none' }}>Remove</span>
      </div>

      <Segmented
        block
        value={(cobroke ? row.cid_share_basis : row.commission_basis) || 'PERCENTAGE'}
        onChange={cobroke ? setCidBasis : setBasis}
        options={SHARE_BASIS_OPTIONS}
      />

      <div style={{ display: 'flex', gap: 12 }}>
        <div style={{ flex: 1 }}>
          {/* A co-broke row edits CID's portion; every other row edits their cut.
              The input used to be disabled and pinned at 0 here, which is how a
              sale reached the engine saying nothing about what CID actually got. */}
          {cobroke ? (cidFlat ? (
            <Input
              type="number"
              inputMode="decimal"
              value={row.cid_share_amount}
              status={error ? 'error' : undefined}
              onChange={(e) => onPatch({ cid_share_amount: e.target.value })}
              prefix="RM"
              placeholder="CID gets"
            />
          ) : (
            <Input
              type="number"
              value={row.cid_share_percentage}
              status={error ? 'error' : undefined}
              onChange={(e) => onPatch({ cid_share_percentage: e.target.value })}
              suffix="%"
              placeholder="CID gets %"
            />
          )) : flat ? (
            <Input
              type="number"
              inputMode="decimal"
              value={row.commission_amount}
              status={error ? 'error' : undefined}
              onChange={(e) => onPatch({ commission_amount: e.target.value })}
              prefix="RM"
              placeholder={referral ? 'Referral fee' : 'Comm amount'}
            />
          ) : (
            <Input
              type="number"
              value={row.commission_percentage}
              status={error ? 'error' : undefined}
              onChange={(e) => onPatch({ commission_percentage: e.target.value })}
              suffix="%"
              placeholder={referral ? 'Referral %' : 'Comm %'}
            />
          )}
        </div>
        {!referral && (
          <Segmented value={row.role} onChange={onRole} options={[{ label: 'We list', value: 'LISTER' }, { label: 'They list', value: 'COBROKE' }]} />
        )}
      </div>
      {error && <div style={{ fontSize: 11.5, color: 'var(--bad)', fontWeight: 600 }}>{error}</div>}
      {/* One hint, not two — a flat referral row would otherwise stack the flat note
          on top of the referral note and say the same thing twice. */}
      {flat && !cobroke && (
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>
          A flat fee — this exact sum comes off net GCI{referral ? ' before agent splits' : ''}, whatever the commission works out to.
        </div>
      )}

      {cobroke && (
        <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>
          They hold the listing and collect the fee. Keep the commission above as the
          <b> whole deal</b> — this is CID&rsquo;s portion of it.
          {split && <><br /><b style={{ color: 'var(--text)' }}>{split}</b></>}
        </div>
      )}

      {referral ? (!flat && (
        <>
          <div>
            <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--muted)', marginBottom: 4 }}>Referral % of</div>
            <Segmented
              block
              value={row.referral_base === 'AFTER_CO_AGENCY' ? 'AFTER_CO_AGENCY' : 'CID_PORTION'}
              onChange={(v) => onPatch({ referral_base: v })}
              options={REFERRAL_BASE_OPTIONS.map((o) => ({ label: o.short, value: o.value }))}
            />
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.45 }}>
            {row.referral_base === 'AFTER_CO_AGENCY'
              ? 'Referral cut of what CID keeps once the We-list co-agencies are paid, before agent splits.'
              : 'Referral cut deducted off the top of net GCI before agent splits.'}
            {baseSummary && <><br /><b style={{ color: 'var(--text)' }}>{baseSummary}</b></>}
          </div>
        </>
      )) : !cobroke && (
        <CoAgencyLetterPicker
          coAgencyId={row.id}
          coAgencyName={row.name}
          value={row.co_agency_letter_id}
          valueLabel={row.co_agency_letter_ref}
          onChange={onLinkLetter}
        />
      )}
    </div>
  );
}

// Letter option — the same facts the admin portal's dropdown shows, stacked instead
// of strung out, so two letters for one co-agency are told apart by property and
// price rather than by ref alone.
const letterOption = (l) => ({
  value: l.id,
  label: l.letter_ref,
  search: `${l.letter_ref || ''} ${l.property_address || ''} ${l.co_agency_agent_name || ''} ${l.letter_type || ''}`,
  data: l,
  sub: (
    <>
      <div>{l.property_address || 'No address'}</div>
      <div>
        {[l.letter_type, l.price_rm != null ? RM(l.price_rm) : null, letterShareSummary(l)]
          .filter(Boolean).join(' · ')}
      </div>
      {(l.co_agency_agent_name || l.letter_date) && (
        <div>
          {[
            l.co_agency_agent_name ? `Agent: ${l.co_agency_agent_name}${l.co_agency_agent_ren_no ? ` (${l.co_agency_agent_ren_no})` : ''}` : null,
            l.letter_date,
          ].filter(Boolean).join(' · ')}
        </div>
      )}
    </>
  ),
});

/**
 * Verified co-agency letters for ONE co-agency, filtered and paged BY THE SERVER.
 * Only the caller's own letters are browsable — authored by them, or naming them as
 * the CID agent. A letter HQ attached from outside that scope is pinned in by id
 * (include_id) so the field can still name what is linked.
 */
function CoAgencyLetterPicker({ coAgencyId, coAgencyName, value, valueLabel, onChange }) {
  const lookup = usePagedOptions(async (q, page) => {
    const r = await lettersApi.listCoAgencyLetters({
      co_agency_id: coAgencyId,
      status: 'VERIFIED',
      search: q || undefined,
      include_id: value || undefined,
      page,
      limit: LETTER_PAGE,
    });
    return (r?.data ?? r ?? []).map(letterOption);
  }, LETTER_PAGE);

  return (
    <>
      <SheetSelect
        title={`Co-agency letter · ${coAgencyName}`}
        placeholder="Link a verified co-agency letter (optional)"
        searchPlaceholder="Search ref, property or agent…"
        value={value || undefined}
        selectedLabel={valueLabel || undefined}
        allowClear
        onSearch={lookup.search}
        onChange={(letterId) => onChange(letterId, lookup.options.find((o) => o.value === letterId)?.data)}
        options={lookup.options}
        onLoadMore={lookup.loadMore}
        hasMore={lookup.hasMore}
        loadingMore={lookup.loadingMore}
        notFoundContent={`No verified letters of yours for ${coAgencyName}.`}
      />
      <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Only your own verified letters — linking one adopts its co-agency share.</div>
    </>
  );
}

// Wizard step indicator (1·2·3) shown in the header on create.
function StepDots({ step }) {
  return (
    <div style={{ display: 'flex', gap: 5 }}>
      {[1, 2, 3].map((n) => {
        const cur = n === step; const done = n < step;
        return (
          <span key={n} style={{
            width: 22, height: 22, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600,
            background: cur ? 'var(--primary)' : done ? 'color-mix(in srgb,var(--primary) 16%,transparent)' : 'transparent',
            color: cur ? '#fff' : done ? 'var(--primary)' : 'var(--muted)',
            border: `1px solid ${cur || done ? 'var(--primary)' : 'var(--border)'}`,
          }}>{n}</span>
        );
      })}
    </div>
  );
}

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 };
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)' };
// `req` renders the admin-portal-style red asterisk on mandatory fields; `hint` is
// the equivalent of AntD's Form.Item `extra` — the line that explains why a field
// is filled in and locked. `error` turns the label red and replaces the hint with
// what has to be fixed; `name` gives the wrapper an id so a failed submit can scroll
// straight to it.
const Field = ({ label, req, hint, error, name, children }) => (
  <div id={name ? `f-${name}` : undefined} style={{ display: 'flex', flexDirection: 'column', gap: 8, scrollMarginTop: 90 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: error ? 'var(--bad)' : 'var(--muted)' }}>
      {req && <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>}{label}
    </label>
    {children}
    {error
      ? <div style={{ fontSize: 11.5, color: 'var(--bad)', fontWeight: 600, marginTop: -2 }}>{error}</div>
      : hint && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: -2 }}>{hint}</div>}
  </div>
);

// LHDN residential tenancy stamp duty: RM1 per RM250 of ANNUAL rent, at a rate
// that steps with the term, plus RM10 for the second stamped copy. Lifted from
// the Tools > Tenancy calculator (TenancyPage.jsx) so the agent gets the figure
// without leaving the form — and so the two can never quote different numbers.
const stampRateFor = (years) => (Number(years || 0) <= 1 ? 1 : Number(years || 0) <= 3 ? 2 : 4);
const estimateStampDuty = (monthlyRent, years) => {
  const rent = Number(monthlyRent || 0);
  if (!(rent > 0)) return 0;
  return Math.ceil((rent * 12) / 250) * stampRateFor(years) + 10;
};

const DUTY_PAYER_OPTIONS = [
  { value: 'AGENT', label: 'I paid it (claim it back)' },
  { value: 'COMPANY', label: 'Company paid LHDN' },
];

/**
 * The tenancy fee a tenant pays bundles the LHDN stamp duty with the agent's
 * admin fee. Which half the agent gets back depends on who fronted the duty:
 *
 *   I paid it     -> the whole fee comes back (admin fee + what I am out of pocket)
 *   Company paid  -> the admin fee alone; CID keeps the rest to settle LHDN
 *
 * Both figures at the bottom are DERIVED — the payable is a generated column in
 * Postgres, so nothing here is ever sent for it. This mirrors the same three
 * rows the admin portal shows, field for field.
 */
function TenancyFeeFields({ fee, setFee, duty, setDuty, paidBy, setPaidBy, dutyAgent, setDutyAgent, saleAgents, locked, monthlyRent, years }) {
  const feeN = fee === '' || fee == null ? null : Number(fee);
  const dutyN = Number(duty || 0);
  const adminFee = feeN == null ? null : Math.max(0, feeN - dutyN);
  const payable = feeN == null ? null : (paidBy === 'AGENT' ? feeN : adminFee);
  const overDuty = feeN != null && dutyN > feeN;
  const estimate = estimateStampDuty(monthlyRent, years);

  return (
    <>
      {locked && (
        <div style={{ fontSize: 11.5, color: 'var(--muted)', background: 'var(--chip)', borderRadius: 8, padding: '7px 9px' }}>
          HQ has verified this breakdown — contact HQ to change it.
        </div>
      )}

      {/* `name` gives this id="f-tenancy_fee" so revealFirst() can jump here —
          recording a stamping-fee receipt is refused until this is filled in, and
          the Receipts screen sends the agent straight to it. */}
      <Field name="tenancy_fee" label="Tenancy fee collected" hint="Total from the tenant — LHDN stamp duty plus your admin fee">
        <MoneyInput value={fee} onChange={setFee} disabled={locked} placeholder="0.00" />
      </Field>

      <Field
        label="Actual LHDN stamp duty"
        hint={overDuty ? undefined : 'What the stamping actually cost — the rest is your admin fee'}
        error={overDuty ? 'Cannot be more than the fee collected' : undefined}
      >
        <MoneyInput value={duty} onChange={setDuty} disabled={locked} placeholder="0.00" status={overDuty ? 'error' : undefined} />
        {!locked && estimate > 0 && (
          <div
            onClick={() => setDuty(String(estimate))}
            style={{ fontSize: 11.5, color: 'var(--primary)', fontWeight: 600, cursor: 'pointer', marginTop: -2 }}
          >
            Calculate — {RM(estimate)} for {years || 1}yr at {RM(monthlyRent || 0)}/mo
          </div>
        )}
      </Field>

      <Field label="Who paid the stamp duty" hint="Decides whether you also claim the duty back on top of your admin fee">
        <SheetSelect
          title="Who paid the stamp duty"
          placeholder="Select"
          value={paidBy}
          onChange={setPaidBy}
          options={DUTY_PAYER_OPTIONS}
          disabled={locked}
        />
      </Field>

      {/* One person is out of pocket, so the reimbursement is repaid to them in
          full rather than split. With a single agent it can only have been them,
          so only ask when the sale actually has a choice. */}
      {paidBy === 'AGENT' && (saleAgents || []).length > 1 && (
        <Field
          name="tenancy_duty_agent"
          label="Which agent paid it"
          hint="The whole reimbursement goes to them — it is not split"
          error={!dutyAgent ? 'Required so the reimbursement reaches the right person' : undefined}
        >
          <SheetSelect
            title="Which agent paid the stamp duty"
            placeholder="Select agent"
            value={dutyAgent}
            onChange={setDutyAgent}
            options={(saleAgents || []).map((a) => ({ value: a.agent_id, label: a.name || 'Agent' }))}
            disabled={locked}
          />
        </Field>
      )}

      <div style={{ background: 'var(--chip)', borderRadius: 10, padding: '10px 11px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: 'var(--muted)' }}>Tenancy admin fee</span>
          <span style={{ fontWeight: 600 }}>{adminFee == null ? '—' : RM(adminFee)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 500 }}>Payable to me</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--primary)' }}>{payable == null ? '—' : RM(payable)}</span>
        </div>
      </div>
    </>
  );
}
